// §S679/§S680 — THE EDITING WORKSPACE. One coherent surface, two distinct modes:
//
//   Magic Wand  — surgical selected-element editing. Arm the wand, point at any
//                 part of the live preview (hover shows a friendly label chip,
//                 the highlight snaps to the thing the edit will actually
//                 change), click it — or pick it from the parts list — and
//                 change JUST that piece through the structured /v1/edit
//                 engine: instant, free, atomic, data-preserving.
//   Pomy        — the broad-change copilot. Whole-app direction, tone,
//                 structure, capabilities. Applies changes through the REAL
//                 revision pipeline (draft-first), answers questions honestly
//                 from the app's real edit surface, sees what the wand is
//                 pointing at, and routes element-level asks to the wand.
//
// Draft/publish law: every edit lands on a DRAFT build; the live app (the URL
// customers hold) changes only when the creator presses Publish. The status
// strip always says which one the preview shows.
//
// Truth law: the parts list comes from the render-truth-filtered edit surface
// (GET /v1/edit/:appId/spec) — the editor never offers an edit that would not
// visibly render. Picking mode is EXPLICIT (§S680): clicks pass through to the
// app until the creator arms the wand, so "try my app" and "edit my app" never
// fight over the same click.
import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { ComponentChildren, RefObject } from 'preact';
import { t, getLocale } from '../lib/i18n';
import type { StreamEvent } from '@vibeshell/shared';
import type { RecentApp, AppVersion } from '../lib/recent-apps';
import { ShareCard } from './ShareCard';
import { PublishControls } from './PublishControls';
import { NextCapabilities } from './NextCapabilities';
import { VersionHistory } from './VersionHistory';
import { ExportSheet } from './ExportSheet';
import { DomainSheet } from './DomainSheet';
import { PaywallModal, type PaywallGate } from './PaywallModal';
import { useAuth } from '@vibeshell/hooks';
import {
  parseInspectorMessage, parseInspectorReady, parseInspectorHover,
  parseSeedRecordId, describeSelection, formatTargetedEdit,
  buildInspectorLabels, genericInspectorLabels, pickedAddress, nodeEditIntent,
  type SelectedElement, type NodeEditOp
} from '../lib/magic-wand';
import { stripMarkdown } from '../lib/markdown';
import { uploadCreatorAsset } from '../lib/creator-assets';
import { readableInk, safeHex } from '../lib/contrast';
import {
  Wand2, Sparkles, Share2, Check, X, History, Loader2, RotateCw, Plus, Trash2, Send,
  UploadCloud, RotateCcw, ExternalLink, Globe, Download, RefreshCcw,
  ArrowUp, ArrowDown, ImagePlus, Sun, Moon, MonitorSmartphone, SlidersHorizontal, Copy,
  ArrowLeft, Palette, Type, Pencil, EyeOff, Link2
} from 'lucide-preact';
import { canonicalShareUrl } from '../lib/share-urls';
import { StylePicker } from './StylePicker';

/**
 * §SLICE-5 — the Style Picker is mounted behind a flag, OFF by default.
 *
 * The picker and its decision logic have existed and been unit-tested for some time; nothing
 * imported the component, so no user could open it. Mounting it is the change. Keeping it flagged
 * is what makes the slice reversible: turning it off restores the editor exactly as it was, with no
 * migration and nothing written to any app that did not pass through it.
 */
const STYLE_PICKER_ENABLED = (import.meta.env?.VITE_STYLE_PICKER ?? '') === 'on';

// ── Server surface types (mirror packages/shared/src/edit-nodes.ts) ────────
interface WireField { field: string; label: string; labelHe: string; kind: 'text' | 'number' | 'money' | 'image'; value: string | number | null }
interface WireNode {
  id: string; kind: 'identity' | 'theme' | 'seedRow' | 'block' | 'tab';
  label: string; labelHe: string; value?: string;
  group: string; groupHe: string;
  collection?: string; rowIndex?: number;
  screenId?: string; blockIndex?: number; textProp?: string;
  fields?: WireField[];
  ops: string[];
}
interface WireSurface {
  engine: 'atlas-v2' | 'legacy';
  nodes: WireNode[];
  collections: Array<{ collection: string; label: string; labelHe: string; count: number }>;
  /** the app's current Style Pack, or null for a legacy app that carries none */
  style?: { packId: string; intensity: string; themeMode: string | null } | null;
  /** for a legacy app, the opt-in migration PROPOSAL — never an application */
  styleMigration?: { verdict: string; proposedPackId: string | null; reasons: string[]; changesAppearance: boolean } | null;
}
interface SurfaceResp {
  ok?: boolean; engine?: string; surface?: WireSurface;
  hasDraft?: boolean; draftAppId?: string | null; draftUpdatedAt?: number | null; publishedAt?: number | null;
  error?: string;
}

/** Waits (ms) between reads of an edit surface whose app row is not readable yet: ~10.8s in all. */
const SURFACE_RETRY_MS = [600, 1200, 2000, 3000, 4000] as const;

const heMode = (): boolean => getLocale() === 'he';

const nLabel = (n: { label: string; labelHe: string }): string => (heMode() ? n.labelHe : n.label);

// Previews run CROSS-ORIGIN on prod (see PreviewPanel for the full rationale).
const PREVIEW_ORIGIN =
  typeof window !== 'undefined' && /(^|\.)pomela\.app$/.test(window.location.hostname)
    ? 'https://vibeshell-generation.mike1973bou.workers.dev'
    : '';
function toPreviewSrc(url: string): string {
  if (!PREVIEW_ORIGIN || !url) return url;
  try {
    const u = new URL(url, window.location.origin);
    return PREVIEW_ORIGIN + u.pathname + u.search + u.hash;
  } catch {
    return url;
  }
}

// Curated accent choices (brand-tested; custom hex still allowed).
const ACCENTS = ['#F4477E', '#7C4DFF', '#0F8A7E', '#E0457B', '#3B6DFF', '#F59E0B', '#10B981', '#111827'];

type WandTarget =
  | { kind: 'identity'; nodeId: 'meta:name' | 'meta:tagline'; value: string }
  | { kind: 'accent' }
  | { kind: 'mode'; value: string }
  | { kind: 'tab'; screenId: string; value: string }
  | { kind: 'seedRow'; collection: string; rowIndex: number }
  | { kind: 'newRow'; collection: string }
  | { kind: 'userRecord' }
  | { kind: 'free'; element: SelectedElement };

/** §Pomy-routing — a targeted node edit the copilot proposed for a turn: the
 *  EXACT structured intent (op + verbatim value/style + address) plus its credit
 *  class ('free' for a structured node edit). The editor renders it as an Apply
 *  card and, on Apply, POSTs it through the SAME applyIntent path the Wand /
 *  Customize controls use — no new fetch, the value preserved byte-for-byte.
 *  `applied` flips the card to its done state after a successful apply. */
interface PomyNodeEdit { op: NodeEditOp; address: string; value?: string | boolean; style?: Record<string, string> }
interface PomyMsg { role: 'user' | 'assistant'; content: string; nodeEdit?: PomyNodeEdit; cost?: string; applied?: boolean }

/** The address ('pom:…' | 'row:…') a wand target answers to — used for the
 *  two-way hover link between the parts list and the preview. */
function targetAddress(tgt: WandTarget | null): string {
  if (!tgt) return '';
  if (tgt.kind === 'identity') return `pom:${tgt.nodeId}`;
  if (tgt.kind === 'accent') return 'pom:theme:primary';
  if (tgt.kind === 'tab') return `pom:tab:${tgt.screenId}`;
  if (tgt.kind === 'seedRow') return `row:seed_${tgt.collection}_${tgt.rowIndex}`;
  return '';
}
function nodeAddress(n: WireNode): string {
  if (n.kind === 'seedRow' && n.collection != null && n.rowIndex != null) return `row:seed_${n.collection}_${n.rowIndex}`;
  if (n.kind === 'tab' && n.screenId) return `pom:tab:${n.screenId}`;
  return `pom:${n.id}`;
}

export function EditorWorkspace({
  app, events, isRevising, onAnother, onRevise, onCancelRevise, notice, onClearNotice, capabilityNote
}: {
  app: RecentApp;
  events: StreamEvent[];
  isRevising: boolean;
  onAnother: () => void;
  onRevise: (prompt: string, assets?: import('../lib/creator-assets').CreatorAssetWire[]) => void;
  onCancelRevise?: () => void;
  notice?: string;
  onClearNotice?: () => void;
  /** §P2-Y — honest post-build note for a best-effort / not-yet-SUPPORTED build. */
  capabilityNote?: import('@vibeshell/shared').CapabilityNote;
}) {
  // §S6 + §V2 editor patch — the sanctioned editing surfaces (Mike's model),
  // now presented Pomy-FIRST:
  //   'pomy'      — the DEFAULT surface: "tell me what you want to change".
  //                 Broad conversational edits (restructure, tone, capabilities).
  //   'customize' — fast structured/deterministic controls (accent, light/dark,
  //                 name, tagline, tab labels, rows). Instant + free.
  //   'wand'      — the Magic Wand's own tab: teaches + arms the element-picker
  //                 (the floating stage button stays; the picker overlay itself
  //                 is unchanged). A pick made from here shows its editor here.
  //   'share'     — publish / share / export.
  const [tab, setTab] = useState<'pomy' | 'customize' | 'wand' | 'share'>('pomy');
  const [surface, setSurface] = useState<WireSurface | null>(null);
  // §S6 — 'unclaimed' = a guest-built app the signed-in builder hasn't adopted
  // yet (server said 403 error:'unclaimed'); we self-heal it via /adopt. 'forbidden'
  // = genuinely someone else's app. 'unauthed' = truly not signed in (401).
  const [surfacePhase, setSurfacePhase] = useState<'loading' | 'ready' | 'unsupported' | 'unauthed' | 'unclaimed' | 'forbidden' | 'error'>('loading');
  const [claiming, setClaiming] = useState(false);
  const claimTriedRef = useRef(false);
  const [draftInfo, setDraftInfo] = useState<{ hasDraft: boolean; draftAppId: string | null }>({ hasDraft: false, draftAppId: null });
  const [flash, setFlash] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);
  // §P2-Y — the honest post-build note is shown once per build; dismissing hides it for this app.
  const [noteDismissed, setNoteDismissed] = useState(false);
  useEffect(() => { setNoteDismissed(false); }, [app.appId]);
  const [busy, setBusy] = useState<'' | 'apply' | 'publish' | 'discard'>('');
  const [target, setTarget] = useState<WandTarget | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [domainOpen, setDomainOpen] = useState(false);
  const [paywallGate, setPaywallGate] = useState<PaywallGate | null>(null);
  const [confirming, setConfirming] = useState<'' | 'publish' | 'discard'>('');
  // §V2 publish sheet — the go-live moment gets a real state surface
  // (publishing → live-with-link / error-with-retry) instead of a toast.
  const [pubPhase, setPubPhase] = useState<'' | 'publishing' | 'success' | 'error'>('');
  // §S680 wand mode: explicit arm/disarm, inspector-v2 handshake, hover link.
  const [wandArmed, setWandArmed] = useState(false);
  const [inspectorReady, setInspectorReady] = useState(false);
  const [hoverAddr, setHoverAddr] = useState('');
  // §S680 wand → Pomy: the selection Pomy is "looking at".
  const [pomySelection, setPomySelection] = useState<SelectedElement | null>(null);
  const { session, loading: authLoading } = useAuth();
  const isPro = !authLoading && session.plan === 'pro';
  const [isNarrow, setIsNarrow] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const on = () => setIsNarrow(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);

  const flashOk = (msg: string) => { setFlash({ kind: 'ok', msg }); window.setTimeout(() => setFlash((f) => (f?.msg === msg ? null : f)), 3200); };
  const flashErr = (msg: string) => setFlash({ kind: 'err', msg });

  // §V2 editor — Share lives in the ed-top (spike anatomy); its full surface
  // (link, publish visibility, next capabilities, export/domain) opens as a
  // sheet. pomySeed prefills the Chat composer (the pages-rail '+' hand-off).
  const [shareOpen, setShareOpen] = useState(false);
  const [pomySeed, setPomySeed] = useState('');

  // The shell hides its global Pomy fab while the editor owns the surface
  // (spike: .app.editor-mode) — broadcast mount/unmount.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('pomela:editor-mode', { detail: true }));
    return () => window.dispatchEvent(new CustomEvent('pomela:editor-mode', { detail: false }));
  }, []);

  // ── Edit surface (draft-aware) ────────────────────────────────────────
  // A build's app row can be unreadable for a moment after the build finishes (the spec answers 404, the preview 503). That is
  // a race, not an app "built before editing": wait for the row with a bounded backoff, then say plainly that it did not come.
  const loadGen = useRef(0);
  useEffect(() => () => { loadGen.current += 1; }, []);
  const loadSurface = useCallback(async (quiet = false) => {
    const gen = ++loadGen.current;
    if (!quiet) setSurfacePhase('loading');
    try {
      for (let attempt = 0; ; attempt += 1) {
        const res = await fetch(`/v1/edit/${encodeURIComponent(app.appId)}/spec?target=draft`, { credentials: 'include' });
        if (gen !== loadGen.current) return null; // superseded by a newer load, or unmounted
        if ((res.status === 404 || res.status === 503) && attempt < SURFACE_RETRY_MS.length) {
          await new Promise((resolve) => window.setTimeout(resolve, SURFACE_RETRY_MS[attempt]));
          if (gen !== loadGen.current) return null;
          continue;
        }
        if (res.status === 401) { setSurfacePhase('unauthed'); return null; }
        if (res.status === 403) {
          // §S6 — distinguish an orphan (guest-built, adoptable) from a genuine
          // not-yours. The server tags the reason; a signed-in builder's orphan
          // self-heals via the adopt effect below instead of a dead-end message.
          let reason = '';
          try { reason = ((await res.json()) as { error?: string }).error ?? ''; } catch { /* no body */ }
          setSurfacePhase(reason === 'unclaimed' ? 'unclaimed' : 'forbidden');
          return null;
        }
        if (res.status === 409) { setSurfacePhase('unsupported'); return null; }
        if (!res.ok) { setSurfacePhase('error'); return null; }
        const data = (await res.json()) as SurfaceResp;
        setSurface(data.surface ?? null);
        setDraftInfo({ hasDraft: !!data.hasDraft, draftAppId: data.draftAppId ?? null });
        setSurfacePhase(data.surface ? 'ready' : 'error');
        return data;
      }
    } catch {
      if (gen === loadGen.current) setSurfacePhase('error');
      return null;
    }
  }, [app.appId]);

  useEffect(() => { void loadSurface(); }, [loadSurface]);

  // When a revision lands (isRevising falls back to false) the draft pointer
  // and surface changed server-side — refresh both without tearing the
  // workspace down (§S680 in-place revision loop).
  const wasRevising = useRef(isRevising);
  useEffect(() => {
    if (wasRevising.current && !isRevising) {
      void loadSurface(true);
      reloadIframe();
    }
    wasRevising.current = isRevising;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRevising]);

  // ── Preview iframe (draft URL when a draft exists) ────────────────────
  const previewUrl = useMemo(() => {
    if (draftInfo.hasDraft && draftInfo.draftAppId) {
      try {
        const u = new URL(app.url, window.location.origin);
        u.pathname = u.pathname.replace(app.appId, draftInfo.draftAppId);
        return u.pathname + u.search;
      } catch { /* fall through */ }
    }
    return app.url;
  }, [app.url, app.appId, draftInfo]);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [iframeError, setIframeError] = useState(false);
  const frameRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = frameRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w && w > 40) el.style.setProperty('--preview-scale', String(w / 390));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  useEffect(() => { setIframeLoaded(false); setIframeError(false); setInspectorReady(false); }, [previewUrl]);
  useEffect(() => {
    if (iframeLoaded || iframeError) return;
    const timer = window.setTimeout(() => setIframeError(true), 12_000);
    return () => window.clearTimeout(timer);
  }, [previewUrl, iframeLoaded, iframeError]);
  const reloadIframe = useCallback(() => {
    setIframeError(false);
    setIframeLoaded(false);
    setInspectorReady(false);
    const el = iframeRef.current;
    if (el) {
      const src = toPreviewSrc(previewUrl);
      el.src = `${src}${src.includes('?') ? '&' : '?'}_r=${Date.now()}`;
    }
  }, [previewUrl]);

  // §S6 — SELF-HEAL ownership. A guest-built app (accountId NULL) is un-editable
  // after the builder signs in (the server 403s with error:'unclaimed'). The
  // /adopt route claims it IFF this device's signed cookie created it. We try
  // once automatically (invisible fix for the common case), then fall back to an
  // explicit "Claim & edit" button if the auto-claim can't prove ownership.
  const claimAndReload = useCallback(async (): Promise<void> => {
    if (claiming) return;
    setClaiming(true);
    try {
      const res = await fetch(`/v1/apps/${encodeURIComponent(app.appId)}/adopt`, { method: 'POST', credentials: 'include' });
      const j = res.ok ? ((await res.json()) as { owned?: boolean }) : null;
      if (j?.owned) {
        await loadSurface(true);
        reloadIframe();
        return;
      }
      setSurfacePhase('forbidden'); // couldn't prove ownership → honest, not a dead-end loop
    } catch {
      /* leave the phase as-is so the manual button stays available */
    } finally {
      setClaiming(false);
    }
  }, [app.appId, claiming, loadSurface, reloadIframe]);

  // Auto-attempt the claim ONCE per app when a signed-in builder opens their
  // orphan. The ref stops the effect from looping if the claim is refused.
  useEffect(() => { claimTriedRef.current = false; }, [app.appId]);
  useEffect(() => {
    if (surfacePhase === 'unclaimed' && session.signedIn && !claimTriedRef.current && !claiming) {
      claimTriedRef.current = true;
      void claimAndReload();
    }
  }, [surfacePhase, session.signedIn, claiming, claimAndReload]);

  const appOrigin = useMemo(() => {
    try { return new URL(toPreviewSrc(previewUrl), window.location.origin).origin; } catch { return ''; }
  }, [previewUrl]);
  const sandboxFlags = useMemo(() => {
    const flags = ['allow-scripts', 'allow-popups', 'allow-forms'];
    try {
      const parentOrigin = typeof window !== 'undefined' ? window.location.origin : '';
      if (appOrigin && parentOrigin && appOrigin !== parentOrigin) flags.push('allow-same-origin');
    } catch { /* conservative default */ }
    return flags.join(' ');
  }, [appOrigin]);

  const nodesById = useMemo(() => {
    const m = new Map<string, WireNode>();
    for (const n of surface?.nodes ?? []) m.set(n.id, n);
    return m;
  }, [surface]);

  // ── Magic Wand wiring (§S680: explicit picking mode) ──────────────────
  const postToApp = useCallback((msg: Record<string, unknown>) => {
    iframeRef.current?.contentWindow?.postMessage(msg, appOrigin || '*');
  }, [appOrigin]);

  const armed = wandArmed && (tab === 'customize' || tab === 'wand') && iframeLoaded && !isRevising;
  useEffect(() => {
    if (armed) {
      postToApp({
        type: 'pomela:inspect:enable',
        labels: buildInspectorLabels(surface?.nodes ?? [], heMode()),
        generic: genericInspectorLabels()
      });
    } else {
      postToApp({ type: 'pomela:inspect:disable' });
      setHoverAddr('');
    }
    return () => postToApp({ type: 'pomela:inspect:disable' });
  }, [armed, postToApp, surface]);

  // §S680 — a PASSIVE readiness probe once the preview loads on the wand tab.
  // The inspector answers `ping` with `ready` WITHOUT arming (no cursor change,
  // no click hijack), so the wand affordance can appear on apps that support it
  // — instead of the old deadlock where the button was gated on `ready` but
  // `ready` only came after arming, which needed the button. Re-probes a few
  // times in case the iframe's listener attaches just after load.
  useEffect(() => {
    if (!iframeLoaded || inspectorReady) return;
    let tries = 0;
    const ping = () => postToApp({ type: 'pomela:inspect:ping' });
    ping();
    const id = window.setInterval(() => {
      tries += 1;
      if (inspectorReady || tries > 6) { window.clearInterval(id); return; }
      ping();
    }, 500);
    return () => window.clearInterval(id);
  }, [iframeLoaded, inspectorReady, postToApp]);

  // Esc closes the topmost workspace overlay; with nothing open it exits
  // picking mode. History/export/domain/paywall own their Escape — while one
  // of them is above us we stay hands-off so a single press closes one layer.
  useEffect(() => {
    const pubDismissable = pubPhase !== '' && pubPhase !== 'publishing';
    if (!(armed || shareOpen || pubDismissable || confirming !== '')) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (historyOpen || exportOpen || domainOpen || paywallGate) return;
        if (pubDismissable) { setPubPhase(''); return; }
        if (shareOpen) { setShareOpen(false); return; }
        if (confirming !== '') { setConfirming(''); return; }
        setWandArmed(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [armed, shareOpen, pubPhase, confirming, historyOpen, exportOpen, domainOpen, paywallGate]);

  const targetRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function onMessage(e: MessageEvent) {
      // Cross-origin prod previews report their real origin; a same-origin
      // sandboxed dev preview reports 'null' — accept that ONLY when the
      // message provably comes from our own iframe's contentWindow.
      const fromOurFrame = e.source && e.source === iframeRef.current?.contentWindow;
      if (!(appOrigin && e.origin === appOrigin) && !(e.origin === 'null' && fromOurFrame)) return;
      if (parseInspectorReady(e.data) != null) { setInspectorReady(true); return; }
      const hover = parseInspectorHover(e.data);
      if (hover) { setHoverAddr(hover.address); return; }
      const el = parseInspectorMessage(e.data);
      if (!el) return;
      setTab((cur) => (cur === 'wand' ? 'wand' : 'customize'));
      // Address → structured target; no address → honest AI-edit fallback.
      if (el.pom === 'meta:name') { setTarget({ kind: 'identity', nodeId: 'meta:name', value: nodesById.get('meta:name')?.value ?? el.text }); return; }
      if (el.pom === 'meta:tagline') { setTarget({ kind: 'identity', nodeId: 'meta:tagline', value: nodesById.get('meta:tagline')?.value ?? el.text }); return; }
      if (el.pom && el.pom.startsWith('tab:')) {
        const screenId = el.pom.slice(4);
        const n = nodesById.get(`tab:${screenId}`);
        setTarget({ kind: 'tab', screenId, value: n?.value ?? el.text.slice(0, 24) });
        return;
      }
      const seed = parseSeedRecordId(el.recordId, el.collection);
      if (seed && nodesById.has(`seed:${seed.collection}:${seed.rowIndex}`)) { setTarget({ kind: 'seedRow', ...seed }); return; }
      if (el.recordId && !el.recordId.startsWith('seed_')) { setTarget({ kind: 'userRecord' }); return; }
      setTarget({ kind: 'free', element: el });
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [appOrigin, nodesById]);

  // Selected-from-preview → make sure the editor is visible (desktop panel
  // scroll; on narrow screens the target renders as a bottom sheet instead).
  useEffect(() => {
    if (target && !isNarrow) {
      requestAnimationFrame(() => targetRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }));
    }
  }, [target, isNarrow]);

  const closeTarget = useCallback(() => {
    setTarget(null);
    postToApp({ type: 'pomela:inspect:clear' });
  }, [postToApp]);

  // Parts-list hover → pulse the matching element inside the preview.
  const previewHighlight = useCallback((address: string) => {
    if (armed && address) postToApp({ type: 'pomela:inspect:highlight', address });
  }, [armed, postToApp]);

  // ── Structured edit application ───────────────────────────────────────
  const applyIntent = useCallback(async (intent: Record<string, unknown>, okMsg?: string): Promise<boolean> => {
    setBusy('apply');
    try {
      const res = await fetch('/v1/edit', {
        method: 'POST', credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ appId: app.appId, intent, target: 'draft' })
      });
      if (res.ok) {
        const j = (await res.json()) as { draftAppId?: string };
        if (j.draftAppId) setDraftInfo({ hasDraft: true, draftAppId: j.draftAppId });
        await loadSurface(true);
        flashOk(okMsg ?? t('ew.applied'));
        window.posthog?.capture('wand_structured_edit', { appId: app.appId, op: String(intent.op) });
        return true;
      }
      // §S6 — an ownership 403 is NOT a content rejection; surface the real
      // reason (claim / not-yours / sign-in) instead of the reassuring flash.
      if (res.status === 401) { setSurfacePhase('unauthed'); return false; }
      if (res.status === 403) {
        let reason = '';
        try { reason = ((await res.json()) as { error?: string }).error ?? ''; } catch { /* no body */ }
        setSurfacePhase(reason === 'unclaimed' ? 'unclaimed' : 'forbidden');
        return false;
      }
      flashErr(t('ew.applyFailed'));
      return false;
    } catch {
      flashErr(t('ew.applyFailed'));
      return false;
    } finally {
      setBusy('');
    }
  }, [app.appId, loadSurface]);

  // ── Publish / discard (styled inline confirms — no native dialogs) ────
  // §V2 — publish drives the sheet's honest states: 'publishing' while the
  // REAL request is in flight, 'success' only on a 2xx (with the live link),
  // 'error' with a retry that re-runs the same call. No timers, no faking.
  const publish = useCallback(async () => {
    setConfirming('');
    setBusy('publish');
    setPubPhase('publishing');
    try {
      const res = await fetch(`/v1/apps/${encodeURIComponent(app.appId)}/publish`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        setDraftInfo({ hasDraft: false, draftAppId: null });
        await loadSurface(true);
        setPubPhase('success');
        window.posthog?.capture('draft_published', { appId: app.appId });
      } else {
        setPubPhase('error');
      }
    } catch {
      setPubPhase('error');
    } finally {
      setBusy('');
    }
  }, [app.appId, loadSurface]);

  const discard = useCallback(async () => {
    setConfirming('');
    setBusy('discard');
    try {
      const res = await fetch(`/v1/apps/${encodeURIComponent(app.appId)}/draft`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) {
        setDraftInfo({ hasDraft: false, draftAppId: null });
        setTarget(null);
        await loadSurface(true);
        flashOk(t('ew.discarded'));
      } else {
        // §honesty (2026-07-12) — a failed discard must not look like nothing happened; the draft is
        // still there, so tell the user instead of silently resetting busy.
        flashErr(t('ew.actionFailed'));
      }
    } catch {
      flashErr(t('ew.actionFailed'));
    } finally {
      setBusy('');
    }
  }, [app.appId, loadSurface]);

  // Restore a version — adopt that immutable build as the DRAFT (publish makes
  // it live); restoring the original just discards the draft.
  const restoreVersion = useCallback(async (v: AppVersion) => {
    setHistoryOpen(false);
    if (v.appId === app.appId) { await discard(); return; }
    try {
      const res = await fetch(`/v1/apps/${encodeURIComponent(app.appId)}/draft`, {
        method: 'POST', credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ draftAppId: v.appId })
      });
      if (res.ok) {
        setDraftInfo({ hasDraft: true, draftAppId: v.appId });
        await loadSurface(true);
        flashOk(t('ew.versionRestoreDraft'));
        window.posthog?.capture('version_restored', { appId: v.appId });
      } else {
        // §honesty — a failed restore must surface, not look like nothing happened.
        flashErr(t('ew.actionFailed'));
      }
    } catch { flashErr(t('ew.actionFailed')); }
  }, [app.appId, discard, loadSurface]);

  const shareUrl = useMemo(() => {
    try { return new URL(app.url, window.location.origin).href; } catch { return app.url; }
  }, [app.url]);

  // §S679 — canonical share identity (slug/ownerHandle) from the server row;
  // ShareCard copies the /r/ deep link once it exists, else the preview URL.
  const [shareIdent, setShareIdent] = useState<{ slug?: string; ownerHandle?: string }>({});
  useEffect(() => {
    let alive = true;
    void fetch(`/v1/creator/apps/${encodeURIComponent(app.appId)}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        const a = (j as { app?: { slug?: string; ownerHandle?: string } } | null)?.app;
        if (alive && a) setShareIdent({ slug: a.slug, ownerHandle: a.ownerHandle });
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [app.appId]);

  const versions = app.versions;
  const identityNodes = (surface?.nodes ?? []).filter((n) => n.kind === 'identity' || n.kind === 'theme');
  const tabNodes = (surface?.nodes ?? []).filter((n) => n.kind === 'tab');
  const rowNodes = (surface?.nodes ?? []).filter((n) => n.kind === 'seedRow');
  const blockNodes = (surface?.nodes ?? []).filter((n) => n.kind === 'block');
  const rowsByCollection = useMemo(() => {
    const m = new Map<string, WireNode[]>();
    for (const n of rowNodes) {
      if (!n.collection) continue;
      const arr = m.get(n.collection) ?? [];
      arr.push(n);
      m.set(n.collection, arr);
    }
    return m;
  }, [rowNodes]);

  const targetEditor = target && (
    <TargetEditor
      target={target}
      nodesById={nodesById}
      busy={busy === 'apply'}
      applyIntent={applyIntent}
      onDone={closeTarget}
      onAiEdit={(el, instruction) => { onRevise(formatTargetedEdit(el, instruction)); closeTarget(); }}
      onAskPomy={(el) => { setPomySelection(el); setTab('pomy'); closeTarget(); }}
    />
  );

  return (
    <section className="ew ed-root">
      {/* ── ed-top: the spike's editor header — identity · draft truth ·
             history/refresh/new · Share · Publish ── */}
      <div className="ed-top">
        <button
          type="button"
          className="icon-btn ed-back"
          title={t('ew.backToApp')}
          aria-label={t('ew.backToApp')}
          onClick={() => { location.hash = `apps/${app.appId}`; }}
        >
          <ArrowLeft size={16} className="icon" />
        </button>
        <div className="ed-app">
          <span className="ed-ic">{(app.manifest.name?.charAt(0) || 'P').toUpperCase()}</span>
          <div><b>{app.manifest.name?.trim() || app.appId}</b></div>
        </div>
        {/* §UX-VIEWPORT — the live/draft state model lives HERE (toolbar), never as
            an overlay on the phone: which version is shown + where the edit lands +
            whether Publish is still required. */}
        <div
          className={`ed-status ${draftInfo.hasDraft ? 'ed-status--draft' : 'ed-status--live'}`}
          role="status"
          aria-live="polite"
        >
          <span className="ed-status-dot" aria-hidden="true" />
          <span className="ed-status-main">
            {busy === 'publish' || pubPhase === 'publishing'
              ? t('ew.publishing')
              : draftInfo.hasDraft ? t('ew.statusDraft') : t('ew.statusLive')}
          </span>
          <span className="ed-status-sub only-desk">
            {draftInfo.hasDraft ? t('ew.previewingDraft') : t('ew.previewingLive')}
          </span>
        </div>
        <div className="ed-tools only-desk">
          <button type="button" className="icon-btn sm" title={t('ew.history')} onClick={() => setHistoryOpen(true)}>
            <History size={14} className="icon" />
          </button>
          <button type="button" className="icon-btn sm" title={t('ew.refreshPreview')} onClick={reloadIframe}>
            <RefreshCcw size={14} className="icon" />
          </button>
          <button type="button" className="icon-btn sm" title={t('ew.buildAnother')} onClick={onAnother}>
            <Plus size={15} className="icon" />
          </button>
        </div>
        <div className="ed-actions">
          {draftInfo.hasDraft && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setConfirming('discard')} disabled={busy !== '' || isRevising}>
              <RotateCcw size={14} /> <span className="hide-sm">{t('ew.discard')}</span>
            </button>
          )}
          <button type="button" className="btn btn-tonal btn-sm" onClick={() => setShareOpen(true)}>
            <Share2 size={14} /> <span className="hide-sm">{t('ew.tabShare')}</span>
          </button>
          {draftInfo.hasDraft ? (
            <button type="button" className="btn btn-primary" onClick={() => setConfirming('publish')} disabled={busy !== '' || isRevising}>
              {busy === 'publish' ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />}
              <span>{busy === 'publish' ? t('ew.publishing') : t('ew.publish')}</span>
            </button>
          ) : (
            /* honest publish state — nothing to publish; the open-live action
               carries the "it's out there" affordance */
            <button type="button" className="btn btn-primary" onClick={() => window.open(shareUrl, '_blank', 'noopener')}>
              <ExternalLink size={14} /> <span>{t('ew.openLive')}</span>
            </button>
          )}
        </div>
        {confirming !== '' && (
          <div className="ew-confirm" role="alertdialog" aria-modal="false">
            <p className="ew-confirm-t">{confirming === 'publish' ? t('ew.publishConfirmT') : t('ew.discardConfirmT')}</p>
            <p className="ew-confirm-b">{confirming === 'publish' ? t('ew.publishConfirmB') : t('ew.discardConfirmB')}</p>
            <div className="flex items-center justify-end gap-2 pt-1">
              <button type="button" className="btn btn-ghost" onClick={() => setConfirming('')}>{t('ew.keepEditing')}</button>
              {confirming === 'publish' ? (
                <button type="button" className="btn btn-primary" onClick={() => void publish()}>
                  <UploadCloud size={13} /> {t('ew.publishGo')}
                </button>
              ) : (
                <button type="button" className="btn btn-primary" onClick={() => void discard()}>
                  <Trash2 size={13} /> {t('ew.discardGo')}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {flash && (
        <div role={flash.kind === 'err' ? 'alert' : 'status'} className={`ew-flash ew-flash--${flash.kind}`}>
          {flash.kind === 'ok' ? <Check size={14} /> : <X size={14} />} {flash.msg}
          <button type="button" className="ew-flash-x" onClick={() => setFlash(null)} aria-label={t('common.close')}><X size={12} /></button>
        </div>
      )}
      {notice && (
        <div role="status" className="ew-flash ew-flash--err">
          {notice}
          <button type="button" className="ew-flash-x" onClick={onClearNotice} aria-label={t('common.close')}><X size={12} /></button>
        </div>
      )}

      {/* §P2-Y — honest post-build note. Informational (NOT an error): a best-effort build of a
          not-yet-SUPPORTED category still ships, but we tell the creator up-front what it can't do
          yet + the closest real alternative — sourced from the engine's own routeRequest/composer
          honesty, never a hand-written claim. Dismissible. */}
      {capabilityNote && !noteDismissed && (
        <div role="status" className="ew-flash" style={{ alignItems: 'flex-start', background: 'var(--v2-surface-2, rgba(0,0,0,.03))', color: 'inherit' }}>
          <Sparkles size={14} style={{ flex: '0 0 auto', marginTop: 2, opacity: 0.85 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, marginBottom: capabilityNote.gaps.length ? 4 : 0 }}>{capabilityNote.headline}</div>
            {capabilityNote.gaps.length > 0 && (
              <ul style={{ margin: 0, paddingInlineStart: 18, opacity: 0.9 }}>
                {capabilityNote.gaps.map((g, i) => <li key={i}>{g}</li>)}
              </ul>
            )}
          </div>
          <button type="button" className="ew-flash-x" onClick={() => setNoteDismissed(true)} aria-label={t('common.close')}><X size={12} /></button>
        </div>
      )}

      {/* ── ed-body: stage · mode panel. The old top "pages rail" (a horizontal
          app-screen tab row) was removed for launch — it was redundant (a screen
          is selected by tapping it in the live preview or from the Customize
          panel's screen list) and cramped on mobile. Adding a page is still a
          real path: ask Pomy. ── */}
      <div className="ed-body">

        <div className="ed-stage">
          <div
            ref={frameRef}
            className={`device ed-device ${armed ? 'ew-frame--armed' : ''}`}
            style={`--app-accent:${safeHex(app.manifest.primaryColor)}`}
          >
            <div className="glass">
              <div className={`preview-loading${iframeLoaded ? ' preview-loading--hidden' : ''}`} aria-hidden={iframeLoaded}>
                <div
                  className="preview-loading__mark"
                  style={{ background: safeHex(app.manifest.primaryColor), color: readableInk(app.manifest.primaryColor) }}
                >
                  {(app.manifest.name?.charAt(0) || 'P').toUpperCase()}
                </div>
                <span className="preview-loading__label">{t('pv.preparing')}</span>
              </div>
              <iframe
                ref={iframeRef}
                src={toPreviewSrc(previewUrl)}
                title={app.manifest.name}
                sandbox={sandboxFlags}
                allow="camera; geolocation; clipboard-write"
                onLoad={() => { setIframeLoaded(true); setIframeError(false); }}
                onError={() => setIframeError(true)}
              />
              {iframeError && (
                <div className="preview-error" role="alert">
                  <p className="preview-error__msg">{t('pv.errReach')}</p>
                  <button type="button" className="btn btn-primary" onClick={reloadIframe}>{t('common.retry')}</button>
                </div>
              )}
              {/* §S680 in-place revision: the workspace stays; the frame reports progress */}
              {isRevising && (
                <div className="ew-revise-overlay" role="status">
                  <Loader2 size={22} className="animate-spin" />
                  <p className="ew-revise-t">{t('ew.reviseOverlayT')}</p>
                  <p className="ew-revise-b">{t('ew.reviseOverlayB')}</p>
                  <div className="ew-revise-bar"><span /></div>
                  {onCancelRevise && (
                    <button type="button" className="btn btn-ghost" onClick={onCancelRevise}>
                      <X size={13} /> {t('common.cancel')}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          {/* §S680/§S6 — the wand announces itself ON the stage and NEVER
              silently vanishes: not-ready = visible but DISABLED + explained. */}
          {(tab === 'customize' || tab === 'wand') && iframeLoaded && !isRevising && (
            <button
              type="button"
              className={`ew-wandbtn ${armed ? 'ew-wandbtn--armed' : ''} ${!inspectorReady ? 'ew-wandbtn--off' : ''}`}
              aria-pressed={armed}
              disabled={!inspectorReady}
              title={inspectorReady ? undefined : t('ew.wandNotReady')}
              onClick={() => { if (inspectorReady) setWandArmed((w) => !w); }}
            >
              <Wand2 size={14} />
              {!inspectorReady ? t('ew.wandArm') : armed ? t('ew.wandArmed') : t('ew.wandArm')}
              {armed && !isNarrow && <kbd className="ew-wandkbd">{t('ew.wandEsc')}</kbd>}
              {armed && <X size={13} aria-hidden="true" />}
            </button>
          )}
        </div>

        <aside className="ed-panel">
          <div className="ep-tabs">
            {/* Pomy leads — the conversational surface is the customer's first door;
                structured Customize and the Wand picker sit behind it. */}
            <div className="segtabs block" role="tablist">
              <button type="button" role="tab" aria-selected={tab === 'pomy'} className={`segtab ${tab === 'pomy' ? 'on' : ''}`} onClick={() => setTab('pomy')}>
                <Sparkles size={14} /> {t('ew.tabPomy')}
              </button>
              <button type="button" role="tab" aria-selected={tab === 'customize'} className={`segtab ${tab === 'customize' ? 'on' : ''}`} onClick={() => setTab('customize')}>
                <SlidersHorizontal size={14} /> {t('ew.tabCustomize')}
              </button>
              <button type="button" role="tab" aria-selected={tab === 'wand'} className={`segtab ${tab === 'wand' ? 'on' : ''}`} onClick={() => setTab('wand')}>
                <Wand2 size={14} /> {t('ew.tabWandShort')}
              </button>
            </div>
          </div>
          <div className="ep-body">
            <div className={`ep-panel on ep-panel--${tab}`}>
              {tab === 'customize' && (
                <CustomizePane
                  phase={surfacePhase}
                  target={isNarrow ? null : target}
                  targetEditor={isNarrow ? null : targetEditor}
                  targetRef={targetRef}
                  setTarget={setTarget}
                  nodesById={nodesById}
                  tabNodes={tabNodes}
                  rowsByCollection={rowsByCollection}
                  blockNodes={blockNodes}
                  collections={surface?.collections ?? []}
                  currentStyle={surface?.style ?? null}
                  styleMigration={surface?.styleMigration ?? null}
                  hoverAddr={hoverAddr}
                  onNodeHover={previewHighlight}
                  canPick={inspectorReady}
                  applyIntent={applyIntent}
                  applyBusy={busy === 'apply'}
                  isRevising={isRevising}
                  onRevise={onRevise}
                  goPomy={() => setTab('pomy')}
                  signedIn={session.signedIn}
                  claiming={claiming}
                  onClaim={() => void claimAndReload()}
                  onRetry={() => void loadSurface()}
                />
              )}
              {tab === 'wand' && (
                <WandModePane
                  phase={surfacePhase}
                  armed={armed}
                  canArm={inspectorReady}
                  onToggle={() => { if (inspectorReady) setWandArmed((w) => !w); }}
                  target={isNarrow ? null : target}
                  targetEditor={isNarrow ? null : targetEditor}
                  targetRef={targetRef}
                  goPomy={() => setTab('pomy')}
                  signedIn={session.signedIn}
                  claiming={claiming}
                  onClaim={() => void claimAndReload()}
                  onRetry={() => void loadSurface()}
                />
              )}
              {tab === 'pomy' && (
                <PomyPane
                  app={app}
                  surface={surface}
                  hasDraft={draftInfo.hasDraft}
                  isRevising={isRevising}
                  onRevise={onRevise}
                  applyIntent={applyIntent}
                  selection={pomySelection}
                  onClearSelection={() => setPomySelection(null)}
                  seedText={pomySeed}
                  onSeedConsumed={() => setPomySeed('')}
                />
              )}
            </div>
          </div>
        </aside>
      </div>

      {/* §S680 — narrow screens: the target editor rides a bottom sheet over
          the preview, so a tap-in-preview visibly answers with an editor. */}
      {isNarrow && target && (
        <div className="ew-sheet-backdrop" onClick={closeTarget}>
          <div className="ew-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="ew-sheet-grab" aria-hidden="true" />
            {targetEditor}
          </div>
        </div>
      )}

      {/* ── the Share surface (spike: an ed-top action, not a tab) — link,
             publish visibility, next capabilities, badge/export/domain ── */}
      {shareOpen && (
        <div className="ew-pub-scrim" onClick={() => setShareOpen(false)}>
          <div className="ew-pub ew-pub--share" role="dialog" aria-modal="true" aria-label={t('ew.shareSheetT')} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between" style={{ marginBottom: 10 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>{t('ew.shareSheetT')}</h3>
              <button type="button" className="ew-flash-x" onClick={() => setShareOpen(false)} aria-label={t('common.close')}><X size={14} /></button>
            </div>
            <div className="flex flex-col gap-4 ew-share-scroll">
              <ShareCard
                appName={app.manifest.name}
                appDescription={app.manifest.description}
                shareUrl={shareUrl}
                appId={app.appId}
                ogImageUrl={app.ogImageUrl}
                prompt={app.prompt}
                slug={shareIdent.slug}
                ownerHandle={shareIdent.ownerHandle}
                onShared={(method) => window.posthog?.capture('preview_shared', { appId: app.appId, method })}
              />
              <PublishControls appId={app.appId} />
              {!isRevising && <NextCapabilities app={app} onRevise={(p) => { setShareOpen(false); onRevise(p); }} />}
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" onClick={() => setPaywallGate('remove-badge')} disabled={isPro} className="btn btn-ghost">
                  {isPro ? <Check size={15} /> : null}
                  {isPro ? t('ew.badgeHidden') : t('ew.removeBadge')}
                </button>
                <button type="button" onClick={() => (isPro ? setExportOpen(true) : setPaywallGate('export-code'))} className="btn btn-ghost">
                  <Download size={15} /> {t('ew.exportCode')}
                </button>
                <button type="button" onClick={() => (isPro ? setDomainOpen(true) : setPaywallGate('custom-domain'))} className="btn btn-ghost">
                  <Globe size={15} /> {t('ew.customDomain')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* §V2 publish sheet — centered glass dialog over a scrim; driven by
          the REAL /publish request lifecycle above. Dismissable except while
          the request is in flight. */}
      {pubPhase !== '' && (
        <div
          className="ew-pub-scrim"
          onClick={() => { if (pubPhase !== 'publishing') setPubPhase(''); }}
        >
          <div className="ew-pub" role="dialog" aria-modal="true" aria-label={t('ew.publish')} onClick={(e) => e.stopPropagation()}>
            {pubPhase === 'publishing' && (
              <div className="ew-pub-state">
                <span className="ew-pub-spin" aria-hidden="true" />
                <h3>{t('ew.pubT')}</h3>
                <p>{t('ew.pubB')}</p>
              </div>
            )}
            {pubPhase === 'success' && (
              <div className="ew-pub-state">
                <span className="ew-pub-ic ew-pub-ic--ok"><Check size={22} strokeWidth={3} /></span>
                <h3>{t('ew.pubOkT')}</h3>
                <p>{t('ew.pubOkB', { name: app.manifest.name?.trim() || app.appId })}</p>
                <button
                  type="button"
                  className="ew-pub-link"
                  onClick={() => {
                    const link = canonicalShareUrl({ appId: app.appId, url: shareUrl, slug: shareIdent.slug, ownerHandle: shareIdent.ownerHandle });
                    void navigator.clipboard?.writeText(link).then(() => flashOk(t('ew.pubCopied'))).catch(() => {});
                  }}
                >
                  <span className="ew-pub-link__k">{t('ew.pubLinkK')}</span>
                  <span className="ew-pub-link__v">{canonicalShareUrl({ appId: app.appId, url: shareUrl, slug: shareIdent.slug, ownerHandle: shareIdent.ownerHandle }).replace(/^https?:\/\//, '')}</span>
                  <Copy size={14} aria-hidden="true" />
                </button>
                <div className="ew-pub-actions">
                  <button type="button" className="btn btn-primary" onClick={() => window.open(shareUrl, '_blank', 'noopener')}>
                    <ExternalLink size={14} /> {t('ew.pubOpen')}
                  </button>
                  <button type="button" className="btn btn-ghost" onClick={() => setPubPhase('')}>{t('ew.pubBack')}</button>
                </div>
              </div>
            )}
            {pubPhase === 'error' && (
              <div className="ew-pub-state">
                <span className="ew-pub-ic ew-pub-ic--err"><X size={22} strokeWidth={3} /></span>
                <h3>{t('ew.pubErrT')}</h3>
                <p>{t('ew.pubErrB')}</p>
                <div className="ew-pub-actions">
                  <button type="button" className="btn btn-ghost" onClick={() => setPubPhase('')}>{t('common.close')}</button>
                  <button type="button" className="btn btn-primary" onClick={() => void publish()}>
                    <RotateCcw size={14} /> {t('ew.pubRetry')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <VersionHistory
        open={historyOpen}
        versions={versions}
        currentAppId={draftInfo.draftAppId ?? app.appId}
        onRestore={(v) => void restoreVersion(v)}
        onClose={() => setHistoryOpen(false)}
      />
      {exportOpen && <ExportSheet app={app} onClose={() => setExportOpen(false)} />}
      {domainOpen && <DomainSheet app={app} onClose={() => setDomainOpen(false)} />}
      <PaywallModal open={paywallGate !== null} gate={paywallGate ?? 'remove-badge'} appName={app.manifest.name} onClose={() => setPaywallGate(null)} />
    </section>
  );
}

// ── Magic Wand pane ─────────────────────────────────────────────────────────
/** §V2 editor patch — ONE designed treatment for every not-ready surface
 *  state (the old gates rendered a single bare line in a tall empty panel —
 *  the exact "empty editor" Mike rejected). Shared by Customize + Wand. */
function EditorGate({
  phase, goPomy, signedIn, claiming, onClaim, onRetry
}: {
  phase: 'loading' | 'unsupported' | 'unauthed' | 'unclaimed' | 'forbidden' | 'error';
  goPomy: () => void;
  signedIn: boolean;
  claiming: boolean;
  onClaim: () => void;
  onRetry: () => void;
}) {
  if (phase === 'loading') {
    return (
      <div className="flex flex-col gap-2" aria-busy="true">
        {[0, 1, 2].map((i) => <div key={i} className="h-11 rounded-xl bg-[color:var(--bg-subtle)] animate-pulse" />)}
        <p className="text-[12px] text-[color:var(--fg-muted)]">{t('ew.loadingSurface')}</p>
      </div>
    );
  }
  // §S6 — a guest-built app the signed-in builder hasn't adopted yet; the
  // auto-claim usually resolves this before it renders (kept verbatim).
  if (phase === 'unclaimed') {
    return (
      <div className="ew-editor" style={{ borderColor: 'var(--color-primary)' }}>
        <p className="ew-editor-h"><Wand2 size={13} /> {t('ew.claimTitle')}</p>
        <p className="ew-note">{t('ew.claimBody')}</p>
        <button type="button" className="btn btn-primary self-start" disabled={claiming || !signedIn} onClick={onClaim}>
          {claiming ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
          {claiming ? t('ew.claiming') : t('ew.claimCta')}
        </button>
        {!signedIn && <p className="ew-tiny">{t('ew.notSignedIn')}</p>}
      </div>
    );
  }
  if (phase === 'unauthed') {
    return (
      <div className="ew-gate" data-gate="unauthed">
        <span className="ew-gate-ic"><Wand2 size={17} /></span>
        <h4 className="ew-gate-t">{t('ew.gateSigninT')}</h4>
        <p className="ew-gate-b">{t('ew.gateSigninB')}</p>
        <div className="ew-gate-acts">
          <button type="button" className="btn btn-primary" onClick={() => { location.hash = 'signin'; }}>
            {t('nav.signIn')}
          </button>
          <button type="button" className="btn btn-ghost" onClick={goPomy}>
            <Sparkles size={14} /> {t('ew.gateAskPomy')}
          </button>
        </div>
        {/* what editing unlocks — the REAL supported control set, no promises */}
        <ul className="ew-gate-list">
          <li>{t('ew.gateCap1')}</li>
          <li>{t('ew.gateCap2')}</li>
          <li>{t('ew.gateCap3')}</li>
          <li>{t('ew.gateCap4')}</li>
        </ul>
      </div>
    );
  }
  if (phase === 'forbidden') {
    return (
      <div className="ew-gate" data-gate="forbidden">
        <span className="ew-gate-ic"><Wand2 size={17} /></span>
        <h4 className="ew-gate-t">{t('ew.gateForbiddenT')}</h4>
        <p className="ew-gate-b">{t('ew.notYourApp')}</p>
        <div className="ew-gate-acts">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => { window.dispatchEvent(new CustomEvent('pomela:new-project')); location.hash = ''; }}
          >
            <Plus size={14} /> {t('ew.gateBuildOwn')}
          </button>
          <button type="button" className="btn btn-ghost" onClick={goPomy}>
            <Sparkles size={14} /> {t('ew.gateAskPomy')}
          </button>
        </div>
      </div>
    );
  }
  // the read failed (the app's row never became readable, the server or the network): say so, and offer the read again
  if (phase === 'error') {
    return (
      <div className="ew-gate" data-gate="error" role="alert">
        <span className="ew-gate-ic"><RotateCw size={17} /></span>
        <h4 className="ew-gate-t">{t('ew.gateErrorT')}</h4>
        <p className="ew-gate-b">{t('ew.gateErrorB')}</p>
        <div className="ew-gate-acts">
          <button type="button" className="btn btn-primary" onClick={onRetry}><RotateCw size={14} /> {t('common.retry')}</button>
          <button type="button" className="btn btn-ghost" onClick={goPomy}><Sparkles size={14} /> {t('ew.gateAskPomy')}</button>
        </div>
      </div>
    );
  }
  // unsupported (409): an app built before structured editing — Pomy is the real path forward
  return (
    <div className="ew-gate" data-gate="unsupported">
      <span className="ew-gate-ic"><Sparkles size={17} /></span>
      <h4 className="ew-gate-t">{t('ew.gateUnsupportedT')}</h4>
      <p className="ew-gate-b">{t('ew.editUnavailable')}</p>
      <div className="ew-gate-acts">
        <button type="button" className="btn btn-primary" onClick={goPomy}><Sparkles size={14} /> {t('ew.gateAskPomy')}</button>
      </div>
    </div>
  );
}

/** §V2 editor patch — the Magic Wand's own surface. Explains the flow in four
 *  steps, arms/disarms the SAME picker the stage button controls, and hosts
 *  the picked element's editor when the pick was made from here. */
function WandModePane({
  phase, armed, canArm, onToggle, target, targetEditor, targetRef, goPomy, signedIn, claiming, onClaim, onRetry
}: {
  phase: 'loading' | 'ready' | 'unsupported' | 'unauthed' | 'unclaimed' | 'forbidden' | 'error';
  armed: boolean;
  canArm: boolean;
  onToggle: () => void;
  target: WandTarget | null;
  targetEditor: ComponentChildren;
  targetRef: RefObject<HTMLDivElement>;
  goPomy: () => void;
  signedIn: boolean;
  claiming: boolean;
  onClaim: () => void;
  onRetry: () => void;
}) {
  if (phase !== 'ready') {
    return <EditorGate phase={phase} goPomy={goPomy} signedIn={signedIn} claiming={claiming} onClaim={onClaim} onRetry={onRetry} />;
  }
  return (
    <div className="stack-cz">
      {/* the spike's "Point at anything." banner */}
      <div className="banner info">
        <span className="b-ic"><Wand2 size={15} className="icon" /></span>
        <div>
          <b>{t('ew.wandBannerT')}</b>
          <p>{t('ew.wandBannerB')}</p>
        </div>
      </div>

      {target && (
        <div ref={targetRef}>
          {targetEditor}
        </div>
      )}

      <div>
        <button
          type="button"
          className={`ew-wandbtn ew-wandbtn--panel ${armed ? 'ew-wandbtn--armed' : ''} ${!canArm ? 'ew-wandbtn--off' : ''}`}
          aria-pressed={armed}
          disabled={!canArm}
          onClick={onToggle}
        >
          <Wand2 size={15} />
          {armed ? t('ew.wandArmed') : t('ew.wandTurnOn')}
          {armed && <X size={13} aria-hidden="true" />}
        </button>
        <p className="help" style="text-align:center;margin-top:8px">
          {!canArm ? t('ew.wandNotReadyPane') : armed ? t('ew.wandLive') : t('ew.wandHelp')}
        </p>
      </div>

      <div>
        <span className="sec-label" style="display:block;margin-bottom:10px">{t('ew.wandYouCan')}</span>
        <div className="list divide">
          <div className="lrow">
            <span className="l-ico"><Type size={15} className="icon" /></span>
            <div className="l-txt"><b>{t('ew.wandCanTextT')}</b><span>{t('ew.wandCanTextS')}</span></div>
          </div>
          <div className="lrow">
            <span className="l-ico"><Palette size={15} className="icon" /></span>
            <div className="l-txt"><b>{t('ew.wandCanColorT')}</b><span>{t('ew.wandCanColorS')}</span></div>
          </div>
          <div className="lrow">
            <span className="l-ico"><ImagePlus size={15} className="icon" /></span>
            <div className="l-txt"><b>{t('ew.wandCanImageT')}</b><span>{t('ew.wandCanImageS')}</span></div>
          </div>
          <div className="lrow">
            <span className="l-ico"><SlidersHorizontal size={15} className="icon" /></span>
            <div className="l-txt"><b>{t('ew.wandCanSizeT')}</b><span>{t('ew.wandCanSizeS')}</span></div>
          </div>
        </div>
      </div>
      <p className="ew-tiny">{t('ew.wandUndoNote')}</p>
    </div>
  );
}

function CustomizePane({
  phase, target, targetEditor, targetRef, setTarget, nodesById, tabNodes, rowsByCollection, blockNodes,
  collections, currentStyle, styleMigration, hoverAddr, onNodeHover, canPick, applyIntent, applyBusy, isRevising, onRevise, goPomy, signedIn, claiming, onClaim, onRetry
}: {
  phase: 'loading' | 'ready' | 'unsupported' | 'unauthed' | 'unclaimed' | 'forbidden' | 'error';
  target: WandTarget | null;
  targetEditor: ComponentChildren;
  targetRef: RefObject<HTMLDivElement>;
  setTarget: (t: WandTarget | null) => void;
  nodesById: Map<string, WireNode>;
  tabNodes: WireNode[];
  rowsByCollection: Map<string, WireNode[]>;
  blockNodes: WireNode[];
  collections: Array<{ collection: string; label: string; labelHe: string; count: number }>;
  currentStyle: { packId: string; intensity: string; themeMode: string | null } | null;
  styleMigration: { verdict: string; proposedPackId: string | null; reasons: string[]; changesAppearance: boolean } | null;
  hoverAddr: string;
  onNodeHover: (address: string) => void;
  canPick: boolean;
  applyIntent: (intent: Record<string, unknown>, okMsg?: string) => Promise<boolean>;
  applyBusy: boolean;
  isRevising: boolean;
  onRevise: (prompt: string) => void;
  goPomy: () => void;
  signedIn: boolean;
  claiming: boolean;
  onClaim: () => void;
  onRetry: () => void;
}) {
  if (phase !== 'ready') {
    return <EditorGate phase={phase} goPomy={goPomy} signedIn={signedIn} claiming={claiming} onClaim={onClaim} onRetry={onRetry} />;
  }

  const accentNode = nodesById.get('theme:primary');
  const modeNode = nodesById.get('theme:mode');
  const nameNode = nodesById.get('meta:name');
  const taglineNode = nodesById.get('meta:tagline');
  const sameHex = (a?: string | null, b?: string) =>
    (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();

  const nodeBtnProps = (n: WireNode) => {
    const addr = nodeAddress(n);
    return {
      className: `ep-row ew-node ${hoverAddr && hoverAddr === addr ? 'ew-node--hot' : ''}`,
      onMouseEnter: () => onNodeHover(addr),
      onFocus: () => onNodeHover(addr)
    };
  };

  // Compact chip-grid for collections of short single-field rows (e.g. time
  // slots) — a wall of full-width rows was the S679 workspace's worst clutter.
  const isCompact = (rows: WireNode[]): boolean =>
    rows.length >= 6 && rows.every((r) => (r.fields?.length ?? 0) <= 1 && (r.value ?? '').length <= 16);

  return (
    <div className="stack-cz">
      <p className="sub-h" style="margin:0">{t('ew.teachCustomize')}</p>
      {canPick && <p className="help" style="margin:-10px 0 0">{t('ew.customizeHint')}</p>}

      {target && (
        <div ref={targetRef}>
          {targetEditor}
        </div>
      )}

      {/* §SLICE-5 STYLE PACK — the picker, mounted. Flag-gated and off by default.
          The picker computes; the commit routes through the SAME setStyle op the Wand uses, so
          there is one code path that can change an app's look and one place that can be wrong. */}
      {STYLE_PICKER_ENABLED && (
        <div className="ep-group">
          <span className="sec-label">{t('ew.stylePack')}</span>
          {/* §MIGRATION — a LEGACY app is OFFERED a pack. It is never given one: the proposal is
              shown with the recommender's reasons, applying it takes an explicit click, and the
              picker's own Undo removes it again. A migration that ran because a screen was opened
              would be a silent restyle of a live product. */}
          {!currentStyle && styleMigration?.proposedPackId && styleMigration.verdict === 'ELIGIBLE' && (
            <div className="ep-note" data-style-migration-offer>
              <p style="margin:0 0 6px">{t('ew.styleMigrateOffer')}</p>
              {styleMigration.reasons.slice(0, 2).map((r) => (
                <p key={r} className="help" style="margin:0 0 4px">{r}</p>
              ))}
              <button
                type="button"
                className="btn-sm"
                disabled={applyBusy}
                onClick={() => { void applyIntent({ op: 'setStyle', value: styleMigration.proposedPackId }, t('ew.styleApplied')); }}
              >
                {t('ew.styleMigrateApply')}
              </button>
              <p className="help" style="margin:6px 0 0">{t('ew.styleMigrateUndoHint')}</p>
            </div>
          )}
          <StylePicker
            spec={currentStyle ? { style: { stylePackId: currentStyle.packId, styleIntensity: currentStyle.intensity, themeMode: currentStyle.themeMode ?? undefined } } : null}
            onCommit={async (patch) => {
              const st = (patch as { style: { stylePackId?: string; styleIntensity?: string; themeMode?: string } | null }).style;
              // null is the picker's UNDO — it removes the pack and restores the legacy render.
              await applyIntent(st == null
                ? { op: 'setStyle', value: null }
                : { op: 'setStyle', value: st.stylePackId, ...(st.styleIntensity ? { styleIntensity: st.styleIntensity } : {}), ...(st.themeMode ? { themeMode: st.themeMode } : {}) },
              t('ew.styleApplied'));
            }}
            renderPreview={() => null}
          />
        </div>
      )}

      {/* ACCENT — the REAL setThemePrimary op, applied instantly per swatch */}
      {accentNode && (
        <div className="ep-group">
          <span className="sec-label">{nLabel(accentNode)}</span>
          <div className="swatches">
            {ACCENTS.slice(0, 7).map((hex) => (
              <button
                key={hex}
                type="button"
                className={`swatch ${sameHex(accentNode.value, hex) ? 'on' : ''}`}
                style={`--sc:${hex}`}
                aria-label={hex}
                aria-pressed={sameHex(accentNode.value, hex)}
                disabled={applyBusy}
                onClick={() => void applyIntent({ op: 'setThemePrimary', value: hex })}
              />
            ))}
            <button
              type="button"
              className="swatch swatch--custom"
              title={t('ew.accentCustom')}
              aria-label={t('ew.accentCustom')}
              onClick={() => setTarget({ kind: 'accent' })}
            >
              <Plus size={13} className="icon" />
            </button>
          </div>
        </div>
      )}

      {/* APPEARANCE — the REAL setThemeMode op (light / dark / match device) */}
      {modeNode && (
        <div className="ep-group">
          <span className="sec-label">{nLabel(modeNode)}</span>
          <div className="segtabs block">
            {([
              { v: 'light', icon: <Sun size={13} />, lbl: t('ew.modeLight') },
              { v: 'dark', icon: <Moon size={13} />, lbl: t('ew.modeDark') },
              { v: 'auto', icon: <MonitorSmartphone size={13} />, lbl: t('ew.modeAutoShort') }
            ] as const).map((m) => (
              <button
                key={m.v}
                type="button"
                className={`segtab ${(modeNode.value ?? 'auto') === m.v ? 'on' : ''}`}
                aria-pressed={(modeNode.value ?? 'auto') === m.v}
                disabled={applyBusy}
                onClick={() => void applyIntent({ op: 'setThemeMode', value: m.v })}
              >
                {m.icon}{m.lbl}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* IDENTITY — inline real setMeta fields (save on Enter / blur-if-changed) */}
      {(nameNode || taglineNode) && (
        <div className="ep-group">
          <span className="sec-label">{t('ew.identityGroup')}</span>
          {nameNode && (
            <InlineMetaField key={`name:${nameNode.value ?? ''}`} node={nameNode} metaField="name" applyIntent={applyIntent} busy={applyBusy} />
          )}
          {taglineNode && (
            <InlineMetaField key={`tag:${taglineNode.value ?? ''}`} node={taglineNode} metaField="tagline" applyIntent={applyIntent} busy={applyBusy} />
          )}
        </div>
      )}

      {/* STYLE — an honest AI restyle: three directions, each applied by Pomy
          through the REAL revise pipeline (draft-first, uses revise credits).
          No fake instant font toggle — the engine has no structured font op. */}
      <div className="ep-group">
        <span className="sec-label">{t('ew.styleGroup')}</span>
        <div className="segtabs block">
          <button type="button" className="segtab" disabled={isRevising} onClick={() => onRevise(t('ew.stylePromptModern'))}>
            <Type size={13} />{t('ew.styleModern')}
          </button>
          <button type="button" className="segtab" disabled={isRevising} onClick={() => onRevise(t('ew.stylePromptEditorial'))}>
            {t('ew.styleEditorial')}
          </button>
          <button type="button" className="segtab" disabled={isRevising} onClick={() => onRevise(t('ew.stylePromptRounded'))}>
            {t('ew.styleRounded')}
          </button>
        </div>
        <p className="help" style="margin-top:8px">{t('ew.styleNote')}</p>
      </div>

      {/* TABS — real rename rows (tab on/off has no engine op; no fake switches) */}
      {tabNodes.length > 0 && (
        <div className="ep-group">
          <span className="sec-label">{t('ew.tabsGroup')}</span>
          <div className="flex flex-col">
            {tabNodes.map((n) => (
              <button
                key={n.id}
                type="button"
                {...nodeBtnProps(n)}
                onClick={() => setTarget({ kind: 'tab', screenId: n.screenId ?? n.id.slice(4), value: n.value ?? nLabel(n) })}
              >
                <div className="er-txt"><b>{n.value ?? nLabel(n)}</b><span>{t('ew.tabRenameSub')}</span></div>
                <span className="mla" style="color:var(--faint)"><Pencil size={13} className="icon" /></span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* CONTENT — the app's real rows (menu items, services, slots…): the
          heart of Customize for real apps; spike layout, real seed-row ops */}
      {collections.map((c) => {
        const rows = rowsByCollection.get(c.collection) ?? [];
        const compact = isCompact(rows);
        return (
          <div key={c.collection} className="ep-group">
            <div className="flex items-center justify-between">
              <span className="sec-label">{heMode() ? c.labelHe : c.label}</span>
              <button type="button" className="chip" onClick={() => setTarget({ kind: 'newRow', collection: c.collection })}>
                <Plus size={11} /> {t('ew.addItem')}
              </button>
            </div>
            <div className={compact ? 'ew-nodes-grid' : 'flex flex-col'}>
              {rows.map((n) => {
                const priceField = n.fields?.find((f) => f.kind === 'money' || f.field === 'price');
                return compact ? (
                  <button
                    key={n.id}
                    type="button"
                    className={`ew-node ${hoverAddr && hoverAddr === nodeAddress(n) ? 'ew-node--hot' : ''}`}
                    onMouseEnter={() => onNodeHover(nodeAddress(n))}
                    onFocus={() => onNodeHover(nodeAddress(n))}
                    onClick={() => setTarget({ kind: 'seedRow', collection: n.collection!, rowIndex: n.rowIndex! })}
                  >
                    <span className="ew-node-label">{n.value ?? nLabel(n)}</span>
                  </button>
                ) : (
                  <button
                    key={n.id}
                    type="button"
                    {...nodeBtnProps(n)}
                    onClick={() => setTarget({ kind: 'seedRow', collection: n.collection!, rowIndex: n.rowIndex! })}
                  >
                    <div className="er-txt">
                      <b>{n.value ?? nLabel(n)}</b>
                      {priceField?.value != null && priceField.value !== '' && <span>{String(priceField.value)}</span>}
                    </div>
                    <span className="mla" style="color:var(--faint)"><Pencil size={13} className="icon" /></span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* SECTIONS — screen blocks exposed by the surface (free AI edit) */}
      {blockNodes.length > 0 && (
        <div className="ep-group">
          <span className="sec-label">{t('ew.blocksGroup')}</span>
          <div className="flex flex-col">
            {blockNodes.map((n) => (
              <button
                key={n.id}
                type="button"
                {...nodeBtnProps(n)}
                onClick={() => setTarget({ kind: 'free', element: { tag: 'section', classes: '', text: n.value ?? '', pom: n.id, label: nLabel(n) } })}
              >
                <div className="er-txt"><b>{nLabel(n)}</b>{n.value ? <span>{n.value}</span> : null}</div>
                <span className="mla" style="color:var(--faint)"><Wand2 size={13} className="icon" /></span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** Inline identity field — the spike's .field/.input anatomy over the REAL
 *  setMeta op. Saves on Enter, or via the check that appears when dirty. */
function InlineMetaField({
  node, metaField, applyIntent, busy
}: {
  node: WireNode;
  metaField: 'name' | 'tagline';
  applyIntent: (intent: Record<string, unknown>, okMsg?: string) => Promise<boolean>;
  busy: boolean;
}) {
  const [text, setText] = useState(node.value ?? '');
  const dirty = text.trim() !== (node.value ?? '').trim();
  const save = () => {
    if (!dirty || !text.trim()) return;
    void applyIntent({ op: 'setMeta', metaField, value: text.trim() });
  };
  return (
    <div className="field">
      <label>{nLabel(node)}</label>
      <div className="flex items-center gap-2">
        <input
          className="input"
          value={text}
          dir="auto"
          maxLength={metaField === 'name' ? 40 : 90}
          onInput={(e) => setText((e.target as HTMLInputElement).value)}
          onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
        />
        {dirty && (
          <button type="button" className="btn btn-primary btn-sm" disabled={busy || !text.trim()} onClick={save} aria-label={t('ew.saveField')}>
            <Check size={13} />
          </button>
        )}
      </div>
    </div>
  );
}
function TargetEditor({
  target, nodesById, busy, applyIntent, onDone, onAiEdit, onAskPomy
}: {
  target: WandTarget;
  nodesById: Map<string, WireNode>;
  busy: boolean;
  applyIntent: (intent: Record<string, unknown>, okMsg?: string) => Promise<boolean>;
  onDone: () => void;
  onAiEdit: (el: SelectedElement, instruction: string) => void;
  onAskPomy: (el: SelectedElement) => void;
}) {
  const [text, setText] = useState(() =>
    target.kind === 'identity' ? target.value : target.kind === 'tab' ? target.value : ''
  );
  const [fields, setFields] = useState<Record<string, string>>(() => {
    if (target.kind === 'seedRow') {
      const n = nodesById.get(`seed:${target.collection}:${target.rowIndex}`);
      const out: Record<string, string> = {};
      for (const f of n?.fields ?? []) out[f.field] = f.value == null ? '' : String(f.value);
      return out;
    }
    if (target.kind === 'newRow') {
      const sibling = [...nodesById.values()].find((n) => n.kind === 'seedRow' && n.collection === target.collection);
      const out: Record<string, string> = {};
      for (const f of sibling?.fields ?? []) out[f.field] = '';
      if (!Object.keys(out).length) out.name = '';
      return out;
    }
    return {};
  });
  const [customHex, setCustomHex] = useState('');
  const [uploading, setUploading] = useState('');
  // §node-edit — targeted per-element edit inputs (free target with an address).
  const [nodeImg, setNodeImg] = useState('');
  const [nodeAct, setNodeAct] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => { requestAnimationFrame(() => inputRef.current?.focus()); }, []);

  const node = target.kind === 'seedRow' ? nodesById.get(`seed:${target.collection}:${target.rowIndex}`) : undefined;
  const fieldDefs: WireField[] =
    target.kind === 'seedRow'
      ? node?.fields ?? []
      : target.kind === 'newRow'
        ? ([...nodesById.values()].find((n) => n.kind === 'seedRow' && n.collection === target.collection)?.fields ?? [{ field: 'name', label: 'Name', labelHe: 'שם', kind: 'text', value: null }])
        : [];

  if (target.kind === 'userRecord') {
    return (
      <div className="ew-editor">
        <p className="ew-note">{t('ew.userRecordNote')}</p>
        <button type="button" className="btn btn-ghost self-end" onClick={onDone}><X size={13} /> {t('common.close')}</button>
      </div>
    );
  }

  if (target.kind === 'free') {
    // §node-edit — a plain heading/paragraph/image/button carries no pom:/row:
    // identity, but the atlas node-edit layer stamps a POSITIONAL data-node
    // address on it. With an address, offer TARGETED affordances that POST a
    // single setNode* intent (text/image/visibility/style/action) to /v1/edit —
    // element-scoped, apply-to-that-one-node-only. Without one, fall back to a
    // targeted AI revision (unchanged).
    const addr = pickedAddress(target.element);
    const kind = target.element.kind === 'image' ? 'image' : target.element.kind === 'button' ? 'button' : 'text';
    const applyNode = (op: NodeEditOp, arg: { value?: string | boolean; style?: Record<string, string> }, okMsg?: string) => {
      const intent = nodeEditIntent(addr, op, arg);
      if (!intent) return;
      void applyIntent(intent, okMsg).then((ok) => ok && onDone());
    };
    const pickNodeImage = async (file: File) => {
      setUploading('node');
      try {
        const asset = await uploadCreatorAsset(file);
        if (asset.kind === 'image' && asset.url) {
          setNodeImg(asset.url);
          applyNode('setNodeImage', { value: asset.url });
        }
      } catch {
        /* upload failed — the input keeps its value; the creator retries */
      } finally {
        setUploading('');
      }
    };

    if (!addr) {
      // Unaddressable element → honest targeted-AI fallback.
      return (
        <div className="ew-editor" style={{ borderColor: 'var(--color-primary)' }}>
          <p className="ew-editor-h"><Wand2 size={13} /> {t('ew.wandSelected')}: {describeSelection(target.element)}</p>
          <p className="ew-note">{t('ew.noAddress')}</p>
          <input
            ref={inputRef}
            value={text}
            onInput={(e) => setText((e.target as HTMLInputElement).value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && text.trim()) onAiEdit(target.element, text); }}
            placeholder={t('ew.aiEditPlaceholder')}
            className="ew-input"
            dir="auto"
          />
          <p className="ew-tiny">{t('ew.aiEditNote')}</p>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <button type="button" className="btn btn-ghost" onClick={onDone}><X size={13} /> {t('common.cancel')}</button>
              <button type="button" className="btn btn-ghost" onClick={() => onAskPomy(target.element)}>
                <Sparkles size={13} /> {t('ew.pomyAskAbout')}
              </button>
            </div>
            <button type="button" className="btn btn-primary" disabled={!text.trim()} onClick={() => onAiEdit(target.element, text)}>
              <Sparkles size={13} /> {t('ew.aiEditGo')}
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="ew-editor" style={{ borderColor: 'var(--color-primary)' }}>
        <p className="ew-editor-h">
          <Wand2 size={13} /> {t('ew.wandSelected')}: {describeSelection(target.element)}
          <span className="ew-free">{t('ew.wandFreeInstant')}</span>
        </p>
        <p className="ew-tiny">{t('ew.nodeTargetedNote')}</p>

        {/* TEXT — replace this element's copy */}
        {kind === 'text' && (
          <label className="ew-field">
            <span className="ew-field-l">{t('ew.pickText')}</span>
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                value={text}
                onInput={(e) => setText((e.target as HTMLInputElement).value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && text.trim()) applyNode('setNodeText', { value: text.trim() }, t('ew.applied')); }}
                placeholder={target.element.text ? target.element.text : t('ew.nodeTextPlaceholder')}
                className="ew-input flex-1"
                dir="auto"
              />
              <button
                type="button"
                className="btn btn-secondary"
                disabled={busy || !text.trim()}
                onClick={() => applyNode('setNodeText', { value: text.trim() }, t('ew.applied'))}
              >
                {busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} {t('ew.nodeSaveText')}
              </button>
            </div>
          </label>
        )}

        {/* IMAGE — swap the src / background image */}
        {kind === 'image' && (
          <label className="ew-field">
            <span className="ew-field-l">{t('ew.nodeReplaceImage')}</span>
            <div className="flex items-center gap-2">
              {nodeImg
                ? <img src={nodeImg} alt="" className="ew-photo-thumb" />
                : <span className="ew-photo-thumb ew-photo-thumb--empty" aria-hidden="true"><ImagePlus size={14} /></span>}
              <input
                value={nodeImg}
                onInput={(e) => setNodeImg((e.target as HTMLInputElement).value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && nodeImg.trim()) applyNode('setNodeImage', { value: nodeImg.trim() }, t('ew.applied')); }}
                placeholder={t('ew.photoUrlPlaceholder')}
                className="ew-input flex-1"
                dir="ltr"
              />
              <label className={`btn btn-ghost ${uploading === 'node' ? 'opacity-60' : ''}`} style={{ cursor: 'pointer' }}>
                {uploading === 'node' ? <Loader2 size={13} className="animate-spin" /> : <UploadCloud size={13} />}
                {uploading === 'node' ? t('ew.photoUploading') : t('ew.photoUpload')}
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  disabled={uploading !== ''}
                  onChange={(e) => {
                    const file = (e.target as HTMLInputElement).files?.[0];
                    if (file) void pickNodeImage(file);
                  }}
                />
              </label>
            </div>
          </label>
        )}

        {/* ACTION — retarget where a button/link goes */}
        {kind === 'button' && (
          <label className="ew-field">
            <span className="ew-field-l">{t('ew.nodeLink')}</span>
            <div className="flex items-center gap-2">
              <input
                value={nodeAct}
                onInput={(e) => setNodeAct((e.target as HTMLInputElement).value)}
                placeholder={t('ew.nodeLinkPlaceholder')}
                className="ew-input flex-1"
                dir="ltr"
              />
              <button
                type="button"
                className="btn btn-secondary"
                disabled={busy || !/^[a-z0-9_-]{1,60}$/i.test(nodeAct.trim())}
                onClick={() => applyNode('setNodeAction', { value: `nav:${nodeAct.trim()}` }, t('ew.applied'))}
              >
                <Link2 size={13} /> {t('ew.apply')}
              </button>
            </div>
          </label>
        )}

        {/* STYLE — recolor this element's text */}
        <div className="ew-field">
          <span className="ew-field-l">{t('ew.nodeTextColor')}</span>
          <div className="ew-swatches">
            {ACCENTS.map((hex) => (
              <button
                key={hex}
                type="button"
                className="ew-swatch ew-swatch--btn"
                style={{ background: hex }}
                aria-label={hex}
                disabled={busy}
                onClick={() => applyNode('setNodeStyle', { style: { color: hex } }, t('ew.applied'))}
              />
            ))}
          </div>
        </div>

        {/* VISIBILITY + escape hatches */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <button type="button" className="btn btn-ghost" onClick={onDone}><X size={13} /> {t('common.cancel')}</button>
            <button type="button" className="ew-danger" disabled={busy} onClick={() => applyNode('setNodeVisibility', { value: 'hide' }, t('ew.nodeHidden'))}>
              <EyeOff size={13} /> {t('ew.nodeHide')}
            </button>
          </div>
          <button type="button" className="btn btn-ghost" onClick={() => onAskPomy(target.element)}>
            <Sparkles size={13} /> {t('ew.pomyAskAbout')}
          </button>
        </div>
      </div>
    );
  }

  if (target.kind === 'accent') {
    return (
      <div className="ew-editor">
        <p className="ew-editor-h"><Wand2 size={13} /> {t('ew.accent')} <span className="ew-free">{t('ew.wandFreeInstant')}</span></p>
        <div className="ew-swatches">
          {ACCENTS.map((hex) => (
            <button
              key={hex}
              type="button"
              className="ew-swatch ew-swatch--btn"
              style={{ background: hex }}
              aria-label={hex}
              disabled={busy}
              onClick={() => void applyIntent({ op: 'setThemePrimary', value: hex }).then((ok) => ok && onDone())}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input
            value={customHex}
            onInput={(e) => setCustomHex((e.target as HTMLInputElement).value)}
            placeholder="#0F8A7E"
            className="ew-input"
            style={{ maxWidth: 130 }}
            aria-label={t('ew.accentCustom')}
          />
          <button
            type="button"
            className="btn btn-secondary"
            disabled={busy || !/^#[0-9a-fA-F]{6}$/.test(customHex.trim())}
            onClick={() => void applyIntent({ op: 'setThemePrimary', value: customHex.trim() }).then((ok) => ok && onDone())}
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} {t('ew.apply')}
          </button>
          <button type="button" className="btn btn-ghost ms-auto" onClick={onDone}><X size={13} /></button>
        </div>
      </div>
    );
  }

  if (target.kind === 'mode') {
    const modes: Array<{ v: string; label: string; icon: ComponentChildren }> = [
      { v: 'auto', label: t('ew.modeAuto'), icon: <MonitorSmartphone size={14} /> },
      { v: 'light', label: t('ew.modeLight'), icon: <Sun size={14} /> },
      { v: 'dark', label: t('ew.modeDark'), icon: <Moon size={14} /> }
    ];
    return (
      <div className="ew-editor">
        <p className="ew-editor-h"><Wand2 size={13} /> {t('ew.appearance')} <span className="ew-free">{t('ew.wandFreeInstant')}</span></p>
        <div className="flex items-center gap-2">
          {modes.map((m) => (
            <button
              key={m.v}
              type="button"
              className={`btn ${target.value === m.v ? 'btn-secondary' : 'btn-ghost'}`}
              disabled={busy}
              aria-pressed={target.value === m.v}
              onClick={() => void applyIntent({ op: 'setThemeMode', value: m.v }).then((ok) => ok && onDone())}
            >
              {m.icon} {m.label}
            </button>
          ))}
          <button type="button" className="btn btn-ghost ms-auto" onClick={onDone}><X size={13} /></button>
        </div>
      </div>
    );
  }

  if (target.kind === 'tab') {
    return (
      <div className="ew-editor">
        <p className="ew-editor-h"><Wand2 size={13} /> {t('ew.tabRename')} <span className="ew-free">{t('ew.wandFreeInstant')}</span></p>
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            value={text}
            onInput={(e) => setText((e.target as HTMLInputElement).value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && text.trim()) {
                void applyIntent({ op: 'setTabLabel', screenId: target.screenId, value: text.trim() }).then((ok) => ok && onDone());
              }
            }}
            maxLength={24}
            className="ew-input"
            dir="auto"
            aria-label={t('ew.tabRename')}
          />
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || !text.trim()}
            onClick={() => void applyIntent({ op: 'setTabLabel', screenId: target.screenId, value: text.trim() }).then((ok) => ok && onDone())}
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} {t('ew.apply')}
          </button>
        </div>
        <button type="button" className="btn btn-ghost self-end" onClick={onDone}><X size={13} /> {t('common.cancel')}</button>
      </div>
    );
  }

  if (target.kind === 'identity') {
    const label = target.nodeId === 'meta:name' ? t('ew.appName') : t('ew.tagline');
    return (
      <div className="ew-editor">
        <p className="ew-editor-h"><Wand2 size={13} /> {label} <span className="ew-free">{t('ew.wandFreeInstant')}</span></p>
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            value={text}
            onInput={(e) => setText((e.target as HTMLInputElement).value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (target.nodeId !== 'meta:name' || text.trim())) {
                void applyIntent({ op: 'setMeta', metaField: target.nodeId === 'meta:name' ? 'name' : 'tagline', value: text.trim() }).then((ok) => ok && onDone());
              }
            }}
            maxLength={target.nodeId === 'meta:name' ? 40 : 120}
            className="ew-input"
            dir="auto"
            aria-label={label}
          />
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || (target.nodeId === 'meta:name' && !text.trim())}
            onClick={() => void applyIntent({ op: 'setMeta', metaField: target.nodeId === 'meta:name' ? 'name' : 'tagline', value: text.trim() }).then((ok) => ok && onDone())}
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} {t('ew.apply')}
          </button>
        </div>
        <button type="button" className="btn btn-ghost self-end" onClick={onDone}><X size={13} /> {t('common.cancel')}</button>
      </div>
    );
  }

  // seedRow / newRow — the fields editor (batched atomic save §S680)
  const isNew = target.kind === 'newRow';
  const collection = target.collection;
  const rowIndex = target.kind === 'seedRow' ? target.rowIndex : -1;
  const siblingCount = [...nodesById.values()].filter((n) => n.kind === 'seedRow' && n.collection === collection).length;

  async function uploadPhoto(fieldName: string, file: File) {
    setUploading(fieldName);
    try {
      const asset = await uploadCreatorAsset(file);
      if (asset.kind === 'image' && asset.url) {
        setFields((f) => ({ ...f, [fieldName]: asset.url! }));
      }
    } catch {
      /* upload failed — the field keeps its previous value; creator retries */
    } finally {
      setUploading('');
    }
  }

  return (
    <div className="ew-editor">
      <p className="ew-editor-h">
        <Wand2 size={13} /> {isNew ? t('ew.newItem') : (node?.value ?? t('ew.wandSelected'))}
        <span className="ew-free">{t('ew.wandFreeInstant')}</span>
      </p>
      <div className="flex flex-col gap-2">
        {fieldDefs.map((f, i) => (
          <label key={f.field} className="ew-field">
            <span className="ew-field-l">{heMode() ? f.labelHe : f.label}</span>
            {f.kind === 'image' ? (
              <div className="flex items-center gap-2">
                {fields[f.field] ? (
                  <img src={fields[f.field]} alt="" className="ew-photo-thumb" />
                ) : (
                  <span className="ew-photo-thumb ew-photo-thumb--empty" aria-hidden="true"><ImagePlus size={14} /></span>
                )}
                <input
                  value={fields[f.field] ?? ''}
                  onInput={(e) => setFields({ ...fields, [f.field]: (e.target as HTMLInputElement).value })}
                  placeholder={t('ew.photoUrlPlaceholder')}
                  className="ew-input flex-1"
                  dir="ltr"
                />
                <label className={`btn btn-ghost ${uploading === f.field ? 'opacity-60' : ''}`} style={{ cursor: 'pointer' }}>
                  {uploading === f.field ? <Loader2 size={13} className="animate-spin" /> : <UploadCloud size={13} />}
                  {uploading === f.field ? t('ew.photoUploading') : t('ew.photoUpload')}
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    disabled={uploading !== ''}
                    onChange={(e) => {
                      const file = (e.target as HTMLInputElement).files?.[0];
                      if (file) void uploadPhoto(f.field, file);
                    }}
                  />
                </label>
              </div>
            ) : (
              <input
                ref={i === 0 ? inputRef : undefined}
                value={fields[f.field] ?? ''}
                onInput={(e) => setFields({ ...fields, [f.field]: (e.target as HTMLInputElement).value })}
                inputMode={f.kind === 'text' ? undefined : 'decimal'}
                className="ew-input"
                dir="auto"
              />
            )}
          </label>
        ))}
      </div>
      <div className="flex items-center justify-between pt-1">
        {!isNew ? (
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              className="ew-danger"
              disabled={busy}
              onClick={() => void applyIntent({ op: 'removeSeedRow', collection, rowIndex }, t('ew.itemSaved')).then((ok) => ok && onDone())}
            >
              <Trash2 size={13} /> {t('ew.removeItem')}
            </button>
            {siblingCount > 1 && (
              <>
                <button
                  type="button"
                  className="btn btn-ghost"
                  aria-label={t('ew.moveUp')}
                  disabled={busy || rowIndex <= 0}
                  onClick={() => void applyIntent({ op: 'moveSeedRow', collection, rowIndex, direction: 'up' }, t('ew.itemSaved')).then((ok) => ok && onDone())}
                >
                  <ArrowUp size={13} />
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  aria-label={t('ew.moveDown')}
                  disabled={busy || rowIndex >= siblingCount - 1}
                  onClick={() => void applyIntent({ op: 'moveSeedRow', collection, rowIndex, direction: 'down' }, t('ew.itemSaved')).then((ok) => ok && onDone())}
                >
                  <ArrowDown size={13} />
                </button>
              </>
            )}
          </div>
        ) : <span />}
        <div className="flex items-center gap-2">
          <button type="button" className="btn btn-ghost" onClick={onDone}><X size={13} /> {t('common.cancel')}</button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy}
            onClick={async () => {
              if (isNew) {
                const row: Record<string, unknown> = {};
                for (const f of fieldDefs) {
                  const raw = (fields[f.field] ?? '').trim();
                  if (!raw) continue;
                  row[f.field] = f.kind === 'text' || f.kind === 'image' ? raw : Number.isFinite(Number(raw)) ? Number(raw) : raw;
                }
                if (!Object.keys(row).length) return;
                const ok = await applyIntent({ op: 'addSeedRow', collection, row }, t('ew.itemSaved'));
                if (ok) onDone();
                return;
              }
              // §S680 — ONE atomic multi-field save (all-or-nothing server-side)
              const changed: Record<string, unknown> = {};
              for (const f of fieldDefs) {
                const raw = (fields[f.field] ?? '').trim();
                const before = f.value == null ? '' : String(f.value);
                if (raw === before) continue;
                changed[f.field] = f.kind === 'text' || f.kind === 'image' ? raw : raw === '' ? '' : Number.isFinite(Number(raw)) ? Number(raw) : raw;
              }
              if (!Object.keys(changed).length) { onDone(); return; }
              const ok = await applyIntent({ op: 'updateSeedRowFields', collection, rowIndex, fields: changed }, t('ew.itemSaved'));
              if (ok) onDone();
            }}
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} {t('ew.apply')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Pomy pane (the broad-change copilot) ────────────────────────────────────

// §Pomy-routing — the copilot's `nodeEdit` field, validated into the EXACT
// structured intent the /v1/edit engine accepts. The value/style are carried
// through UNCHANGED (the planner already preserved the user's text verbatim);
// we only assert the shape so a malformed field never renders a dead Apply.
const NODE_EDIT_OPS: readonly NodeEditOp[] = ['setNodeText', 'setNodeImage', 'setNodeVisibility', 'setNodeStyle', 'setNodeAction'];
function normalizePomyNodeEdit(raw: unknown): PomyNodeEdit | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  if (typeof r.op !== 'string' || !(NODE_EDIT_OPS as readonly string[]).includes(r.op)) return undefined;
  if (typeof r.address !== 'string' || !r.address.trim()) return undefined;
  const out: PomyNodeEdit = { op: r.op as NodeEditOp, address: r.address };
  if (typeof r.value === 'string' || typeof r.value === 'boolean') out.value = r.value;
  if (r.style && typeof r.style === 'object' && !Array.isArray(r.style)) {
    const style: Record<string, string> = {};
    for (const [k, v] of Object.entries(r.style as Record<string, unknown>)) if (typeof v === 'string') style[k] = v;
    if (Object.keys(style).length) out.style = style;
  }
  return out;
}

// A COMPACT, human description of a proposed node edit: a short op label plus,
// for text/image/link ops, the EXACT new value shown VERBATIM (never paraphrased
// — the same string that will be applied). Visibility/style ops carry no free
// text, so the op label alone (e.g. "Hide this element", "Recolor to #hex") is
// the whole change.
function describePomyNodeEdit(edit: PomyNodeEdit): { op: string; value?: string } {
  switch (edit.op) {
    case 'setNodeText':
      return { op: t('ew.pomyOpText'), value: typeof edit.value === 'string' ? edit.value : undefined };
    case 'setNodeImage':
      return { op: t('ew.pomyOpImage'), value: typeof edit.value === 'string' ? edit.value : undefined };
    case 'setNodeAction':
      return { op: t('ew.pomyOpLink'), value: typeof edit.value === 'string' ? edit.value : undefined };
    case 'setNodeVisibility':
      return { op: (edit.value === true || edit.value === 'hide') ? t('ew.pomyOpHide') : t('ew.pomyOpShow') };
    case 'setNodeStyle': {
      const color = edit.style?.color ?? '';
      return { op: color ? t('ew.pomyOpColor').replace('{color}', color) : t('ew.pomyOpStyle') };
    }
    default:
      return { op: edit.op };
  }
}

function extractEditPrompts(content: string): string[] {
  // §UX — dedup: the same proposed op must never render two Apply cards in one turn.
  const seen = new Set<string>();
  const out: string[] = [];
  const re = /^\s*Edit prompt:\s*(.+?)\s*$/gim;
  let m: RegExpExecArray | null;
  while ((m = re.exec(content))) {
    const s = (m[1] || '').replace(/[*_`"]/g, '').trim();
    const key = s.toLowerCase();
    if (s && !seen.has(key)) { seen.add(key); out.push(s); }
  }
  return out;
}

function PomyPane({
  app, surface, hasDraft, isRevising, onRevise, applyIntent, selection, onClearSelection, seedText, onSeedConsumed
}: {
  app: RecentApp;
  surface: WireSurface | null;
  hasDraft: boolean;
  isRevising: boolean;
  onRevise: (prompt: string) => void;
  /** §Pomy-routing — the SAME structured-edit path the Wand/Customize controls
   *  use (POST /v1/edit target=draft). A proposed nodeEdit applies through this,
   *  never a new fetch. */
  applyIntent: (intent: Record<string, unknown>, okMsg?: string) => Promise<boolean>;
  selection: SelectedElement | null;
  onClearSelection: () => void;
  /** Prefill from another surface (pages-rail '+', gate CTA) — consumed once. */
  seedText?: string;
  onSeedConsumed?: () => void;
}) {
  const storageKey = `pomela:pomy-edit:${app.appId}`;
  const [messages, setMessages] = useState<PomyMsg[]>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter((m) => m && typeof m.content === 'string') : [];
    } catch { return []; }
  });
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  // §Pomy-routing — which turn's nodeEdit Apply is in flight (index into
  // `messages`), so exactly one card shows a spinner + disables while POSTing.
  const [applyingIdx, setApplyingIdx] = useState<number | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (!seedText) return;
    setInput(seedText);
    onSeedConsumed?.();
    window.setTimeout(() => inputRef.current?.focus(), 60);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedText]);
  const [error, setError] = useState<{ msg: string; retryText?: string } | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  // §UX — auto-scroll ONLY when the reader is already near the bottom, so a
  // streamed reply never yanks the view away from an older message the creator
  // scrolled up to read. Sending your own turn always sticks (set on send()).
  const stickRef = useRef(true);
  const onScroll = useCallback((e: Event) => {
    const el = e.currentTarget as HTMLDivElement;
    stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }, []);
  useEffect(() => {
    try {
      if (messages.length === 0) localStorage.removeItem(storageKey);
      else localStorage.setItem(storageKey, JSON.stringify(messages.slice(-30)));
    } catch { /* private mode */ }
    const el = scrollRef.current;
    if (el && stickRef.current) el.scrollTop = el.scrollHeight;
  }, [messages, storageKey]);

  const appContext = useMemo(() => ({
    // §Pomy-routing (#7) — carry the app id so the copilot can derive the app's
    // addressable rendered nodes SERVER-SIDE and resolve a NAMED targeted edit
    // ("change the main headline to X") to a FREE setNodeText even with nothing
    // selected (the editor sends no `elements` list). Server-side guarded +
    // fail-open; a non-atlas / unowned app simply falls back to the model.
    appId: app.appId,
    name: app.manifest.name,
    tagline: (surface?.nodes ?? []).find((n) => n.id === 'meta:tagline')?.value ?? '',
    category: app.manifest.description ?? '',
    collections: (surface?.collections ?? []).map((c) => ({ collection: c.collection, label: c.label, count: c.count })),
    hasDraft,
    // §S680 — richer grounding: pages, accent, app language, wand selection.
    tabs: (surface?.nodes ?? []).filter((n) => n.kind === 'tab').map((n) => n.value ?? n.label).slice(0, 8),
    accent: (surface?.nodes ?? []).find((n) => n.id === 'theme:primary')?.value ?? '',
    appLang: getLocale(),
    // §node-edit — carry the concrete rendered-node address (+ kind) so a
    // natural-language ask on the selected element resolves to a TARGETED
    // setNode* op on THIS one node (select → NL → targeted intent → apply-only).
    selectedElement: selection
      ? {
          label: describeSelection(selection),
          text: selection.text.slice(0, 120),
          address: pickedAddress(selection) || undefined,
          kind: selection.kind || undefined
        }
      : undefined
  }), [app, surface, hasDraft, selection]);

  async function send(text: string) {
    const content = text.trim();
    if (!content || busy) return;
    stickRef.current = true; // sending your own turn always scrolls into view
    const next: PomyMsg[] = [...messages, { role: 'user', content }];
    setMessages(next);
    setInput('');
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/v1/copilot/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ messages: next.slice(-20), mode: 'edit', appContext })
      });
      if (res.status === 429) { setError({ msg: t('ew.pomyRate'), retryText: content }); return; }
      if (res.status === 401 || res.status === 403) { setError({ msg: t('ew.pomySignin') }); return; }
      if (!res.ok) { setError({ msg: t('cop.errServer'), retryText: content }); return; }
      // §Pomy-routing — the copilot may resolve a clear, uniquely-targetable ask
      // to a structured `nodeEdit` (op + verbatim value/style + address) with a
      // `cost` class. Capture both onto the assistant turn so it renders an Apply
      // card wired to the real /v1/edit draft path — instead of the editor
      // dropping the field and Pomy telling the builder to open the Wand tab.
      const data = (await res.json()) as { reply?: string; nodeEdit?: unknown; cost?: string };
      const nodeEdit = normalizePomyNodeEdit(data.nodeEdit);
      const asst: PomyMsg = { role: 'assistant', content: data.reply?.trim() || '…' };
      if (nodeEdit) { asst.nodeEdit = nodeEdit; asst.cost = typeof data.cost === 'string' ? data.cost : undefined; }
      setMessages([...next, asst]);
    } catch {
      setError({ msg: t('cop.errNetwork'), retryText: content });
    } finally {
      setBusy(false);
    }
  }

  // §Pomy-routing — Apply a proposed nodeEdit through the SAME structured path
  // the Wand/Customize controls use (applyIntent → POST /v1/edit target=draft).
  // The intent is passed byte-for-byte (exact value preserved). On success the
  // turn's card flips to its applied state (pending → applied is unmistakable).
  async function applyNodeEdit(idx: number, edit: PomyNodeEdit) {
    if (applyingIdx !== null || isRevising) return;
    setApplyingIdx(idx);
    try {
      const ok = await applyIntent(edit as unknown as Record<string, unknown>, t('ew.applied'));
      if (ok) setMessages((ms) => ms.map((m, i) => (i === idx ? { ...m, applied: true } : m)));
    } finally {
      setApplyingIdx((cur) => (cur === idx ? null : cur));
    }
  }

  const chips = [
    t('ew.chipHeroText'),
    t('ew.pomyChipPremium'),
    getLocale() === 'en' ? t('ew.pomyChipHebrew') : t('ew.chipToEnglish'),
    t('ew.chipAddSection'),
    t('ew.chipImproveDesign'),
    t('ew.chipColors'),
    t('ew.chipReviews'),
    t('ew.chipLaunchReady')
  ];

  return (
    <div className="ew-pomy">
      {selection && (
        <div className="ew-selchip" role="status">
          <Wand2 size={12} />
          <span className="min-w-0 truncate">{t('ew.pomySelChip').replace('{label}', describeSelection(selection))}</span>
          <button type="button" className="ew-flash-x" onClick={onClearSelection} aria-label={t('common.close')}><X size={11} /></button>
        </div>
      )}
      <div ref={scrollRef} onScroll={onScroll} className={`ew-pomy-scroll${messages.length === 0 ? ' ew-pomy-scroll--empty' : ''}`}>
        {messages.length === 0 && (
          <div className="ew-pomy-hero">
            <span className="ew-pomy-hero-av" aria-hidden="true"><Sparkles size={17} /></span>
            <h4 className="ew-pomy-hero-t">{t('ew.pomyHero')}</h4>
            <p className="ew-pomy-hero-b">{t('ew.pomyHeroSub')}</p>
            <span className="sec-label" style="display:block;margin-top:12px">{t('ew.pomyTry')}</span>
            <div className="pomy-chips" style="margin-top:8px">
              {chips.map((c) => (
                <button key={c} type="button" className="pchip" onClick={() => void send(c)}>{c}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`ew-pomy-turn ${m.role === 'user' ? 'ew-pomy-turn--user' : ''}`}>
            {m.role === 'assistant' && <span className="pomi-avatar" aria-hidden="true"><Sparkles size={12} /></span>}
            <div className="min-w-0">
              <div dir="auto" className={m.role === 'user' ? 'cp-user-bubble' : 'ew-pomy-prose'}>
                {stripMarkdown(m.content.split(/\n+/).filter((l) => !/^\s*Edit prompt:/i.test(l)).join('\n'))}
              </div>
              {/* §S680 — never a blind apply: the exact change + its cost, visible */}
              {m.role === 'assistant' && extractEditPrompts(m.content).map((p, j) => (
                <div key={j} className="ew-applycard">
                  <p className="ew-applycard-l">{t('ew.pomyApplyCard')}</p>
                  <p className="ew-applycard-p" dir="auto">{p}</p>
                  <div className="flex items-center justify-between gap-2">
                    <span className="ew-tiny">{t('ew.pomyCost')}</span>
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={isRevising}
                      onClick={() => onRevise(p)}
                    >
                      <Wand2 size={13} /> {t('ew.pomyApply')}
                    </button>
                  </div>
                </div>
              ))}
              {/* §Pomy-routing — a targeted node edit the copilot resolved this
                  turn: the EXACT op + verbatim new value, its FREE/credit class,
                  and one Apply that lands it on the draft via applyIntent. The
                  card flips to a done state after apply (pending vs applied is
                  unmistakable) and never renders twice (one nodeEdit per turn). */}
              {m.role === 'assistant' && m.nodeEdit && (() => {
                const d = describePomyNodeEdit(m.nodeEdit);
                return (
                  <div className={`ew-applycard${m.applied ? ' ew-applycard--applied' : ''}`}>
                    <p className="ew-applycard-l">{m.applied ? t('ew.pomyApplied') : t('ew.pomyApplyCard')}</p>
                    <p className="ew-applycard-op" dir="auto">{d.op}</p>
                    {d.value != null && <p className="ew-applycard-p" dir="auto">{d.value}</p>}
                    <div className="flex items-center justify-between gap-2">
                      <span className="ew-tiny">{m.cost === 'free' ? t('ew.pomyNodeFree') : t('ew.pomyCost')}</span>
                      {m.applied ? (
                        <span className="ew-applycard-done"><Check size={13} /> {t('ew.pomyAppliedTag')}</span>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-primary"
                          disabled={applyingIdx !== null || isRevising}
                          onClick={() => void applyNodeEdit(i, m.nodeEdit!)}
                        >
                          {applyingIdx === i ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />}
                          {t('ew.pomyApply')}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        ))}
        {busy && (
          <div className="ew-pomy-turn">
            <span className="pomi-avatar pomi-avatar--thinking" aria-hidden="true"><Sparkles size={12} /></span>
            <div className="cp-thinking" role="status"><span /><span /><span /></div>
          </div>
        )}
        {isRevising && <p className="ew-note" role="status">{t('ew.pomyApplying')}</p>}
        {error && (
          <p className="ew-note" role="alert">
            {error.msg}
            {error.retryText && (
              <button
                type="button"
                className="ew-linkish"
                onClick={() => {
                  // retry re-sends the same user turn (it's already in the list)
                  const last = error.retryText!;
                  setMessages((ms) => (ms.length && ms[ms.length - 1]!.role === 'user' ? ms.slice(0, -1) : ms));
                  setError(null);
                  void send(last);
                }}
              >
                {t('ew.pomyRetry')}
              </button>
            )}
          </p>
        )}
      </div>
      <div className="chat-input">
        <textarea
          ref={inputRef}
          rows={1}
          value={input}
          onInput={(e) => setInput((e.target as HTMLTextAreaElement).value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(input); } }}
          placeholder={t('ew.pomyPlaceholder')}
          maxLength={1000}
          dir="auto"
        />
        <button type="button" className="chat-send" onClick={() => void send(input)} disabled={busy || !input.trim()} aria-label={t('cop.ariaSend')}>
          <Send size={15} />
        </button>
      </div>
    </div>
  );
}
